import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Send, Bot, User, Globe, Edit2, Copy, RefreshCw, ChevronDown, ChevronRight, Check, Menu, Code, FileText, Palette, Zap, Brain, Users, Share2, CreditCard, Slash, ArrowDown, Square, Settings, CheckCircle, ExternalLink, Calculator, Monitor, Paperclip, X, Image as ImageIcon, Eye, EyeOff, Sparkles, Mail, Target, TrendingUp, Lightbulb, Search, BarChart, AlertTriangle, Mic, Plus, Clock, MessageSquare, Play, FolderOpen, StickyNote, Database } from 'lucide-react';
import MessageRenderer from './MessageRenderer';
import StreamingMessageRenderer from './StreamingMessageRenderer';
import BurstResponse from './BurstResponse';
import ChatWindow from './ChatWindow';
import SlashCommands from './SlashCommands';
import RankBot from './RankBot';
import CreditsModal from './CreditsModal';
import OutOfCreditsModal from './OutOfCreditsModal';
import ModelSelector from './ModelSelector';
import AttachmentDisplay from './AttachmentDisplay';
import ThinkingControl from './ThinkingControl';
import VoiceMessage from './VoiceMessage';
import VoiceModeSelector from './VoiceModeSelector';
import RealtimeAudioOverlay from './RealtimeAudioOverlay';
import NativeRealtimeOverlay from './NativeRealtimeOverlay';
import OpenAISDKOverlay from './OpenAISDKOverlay';
import AIApps from './AIApps';
import { AUTH_BASE_URL } from '../config/api';
import AIAppsWidget from './AIAppsWidget';
import ContextSpacesWidget from './ContextSpacesWidget';
import ContextSpacesPage from './ContextSpacesPage';
import AppsPage from './AppsPage';
import AppOptions from './AppOptions';
import CitationPreview from './CitationPreview';
import CompetitiveAnalysisReport from './CompetitiveAnalysisReport';
import NewFeatureTag from './NewFeatureTag';
import BetaTag from './BetaTag';
import { SeasonalHeaderDecoration } from './seasonal/SeasonalDecorations';
import MCPSelector from './MCPSelector';
import ContextWindowIndicator from './ContextWindowIndicator';
import ContextHealthIndicator from './ContextHealthIndicator';
import { sendChatMessage } from '../services/api';
import { uploadFile } from '../services/fileUploadApi';
import { AI_APPS } from '../constants/aiApps';
import { fetchUserCredits, manageUserCredits } from '../services/usersApi';
import { fetchBurstModes } from '../services/burstModesApi';
import { fetchTopModels } from '../services/modelsApi';
import { generateImage } from '../services/imageGenerationApi';
import { useSocket } from '../contexts/SocketContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useAppearance } from '../contexts/AppearanceContext';
import { useSession } from '../contexts/SessionContext';
import { useMCP } from '../contexts/MCPContext';
import { getContextSpaces } from '../services/contextSpacesApi';

import { 
  fetchModelTools, 
  getProviderFromModel, 
  transformToolsForUI,
  groupToolsByCategory,
  ENHANCED_TOOL_CONFIG 
} from '../services/toolsApi';

// Icon mapping for tools
const ICON_MAP = {
  Globe: Globe,
  Code: Code,
  Palette: Palette,
  Monitor: Monitor,
  FileText: FileText,
  Zap: Zap,
  Calculator: Calculator
};

// Legacy tools for backward compatibility
const LEGACY_TOOLS = [
  {
    id: 'image_generation',
    name: 'Image Gen',
    icon: Palette,
    description: 'Generate or edit images',
    type: 'image_generation',
    bgColor: 'bg-purple-500',
    textColor: 'text-white'
  },
  {
    id: 'code_execution',
    name: 'Code',
    icon: Code,
    description: 'Write and run Python code',
    type: 'code_execution',
    bgColor: 'bg-green-500',
    textColor: 'text-white'
  },
  {
    id: 'web_search',
    name: 'Web Search',
    icon: Globe,
    description: 'Search the web for real-time info',
    type: 'web_search',
    bgColor: 'bg-blue-500',
    textColor: 'text-white'
  },
  {
    id: 'file_search',
    name: 'File Search',
    icon: FileText,
    description: 'Search uploaded files',
    type: 'file_search',
    bgColor: 'bg-orange-500',
    textColor: 'text-white'
  }
];

// Parse special tokens from message content
const parseMessageTokens = (content) => {
  if (!content || typeof content !== 'string') return { content, metadata: {} };
  
  let processedContent = content;
  const metadata = {};
  
  // Extract progress tokens
  const progressMatch = content.match(/__PROGRESS_START__(.*?)(?=__|$)/);
  if (progressMatch) {
    metadata.progressStatus = 'starting';
    metadata.progressText = progressMatch[1];
    processedContent = processedContent.replace(/__PROGRESS_START__.*?(?=__|$)/, '');
  }
  
  const toolAutoMatch = content.match(/__TOOL_AUTO__(.*?)(?=__|$)/);
  if (toolAutoMatch) {
    metadata.progressStatus = 'tool_auto';
    metadata.progressText = toolAutoMatch[1];
    processedContent = processedContent.replace(/__TOOL_AUTO__.*?(?=__|$)/, '');
  }
  
  const streamMatch = content.match(/__STREAM_START__(.*?)(?=__|$)/);
  if (streamMatch) {
    metadata.progressStatus = 'streaming';
    metadata.progressText = streamMatch[1];
    processedContent = processedContent.replace(/__STREAM_START__.*?(?=__|$)/, '');
  }
  
  const toolsReadyMatch = content.match(/__TOOLS_READY__(.*?)(?=__|$)/);
  if (toolsReadyMatch) {
    metadata.progressStatus = 'tools_ready';
    metadata.progressText = toolsReadyMatch[1];
    processedContent = processedContent.replace(/__TOOLS_READY__.*?(?=__|$)/, '');
  }
  
  // Extract web search tokens - but don't remove them from content
  // The MessageRenderer will handle proper display of web search results
  const webSearchMatch = content.match(/__WEB_SEARCH_START__|__WEB_SEARCH_RESULTS_START__/);
  if (webSearchMatch) {
    metadata.webSearchActive = true;
    // Don't remove web search tokens - let MessageRenderer handle them
  }
  
  // Extract thinking tokens
  const thinkingMatch = content.match(/__THINKING_START__(.*?)__THINKING_END__/s);
  if (thinkingMatch) {
    metadata.thinkingContent = thinkingMatch[1];
    metadata.isThinking = false;
    processedContent = processedContent.replace(/__THINKING_START__.*?__THINKING_END__/s, '');
  }
  
  return { content: processedContent.trim(), metadata };
};

const ChatInterface = ({ models, conversation, onUpdateConversation, onToggleSidebar, sidebarCollapsed, currentUser, selectedModel: propSelectedModel, onModelChange, showAIApps: propShowAIApps, setShowAIApps: propSetShowAIApps, onNewConversation, isAppConversation, onOpenMemory, selectedContextSpaceId, contextSpaceContext, showContextSpaces: propShowContextSpaces, setShowContextSpaces: propSetShowContextSpaces, onExitContextSpace, viewingResource, onCloseResourcePreview }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedMCPs } = useMCP();
  const [message, setMessage] = useState('');
  const [pendingMessage, setPendingMessage] = useState(null);
  
  // Debug: Track conversation prop changes
  useEffect(() => {
    console.log('🚀 ChatInterface: conversation prop changed:', {
      conversationId: conversation?.id,
      messagesCount: conversation?.messages?.length || 0,
      conversationReference: conversation,
      messagesArrayReference: conversation?.messages
    });
  }, [conversation]);

  // Handle pending message when conversation is created
  useEffect(() => {
    if (pendingMessage && conversation && (!conversation.messages || conversation.messages.length === 0)) {
      console.log('Conversation ready, sending pending message:', pendingMessage);
      const messageToSend = pendingMessage;
      setPendingMessage(null);
      // Send the message after conversation is ready
      setTimeout(() => {
        setMessage(messageToSend);
        document.querySelector('[data-send-button]')?.click();
      }, 100);
    }
  }, [conversation, pendingMessage]);
  
  // Use prop if available, otherwise use last message model or localStorage
  const getEffectiveModel = () => {
    if (propSelectedModel) return propSelectedModel;
    
    // For existing conversations, use the last assistant message model
    if (conversation?.messages?.length > 0) {
      const lastAssistantMsg = conversation.messages.filter(m => m.role === 'assistant').pop();
      if (lastAssistantMsg?.model) return lastAssistantMsg.model;
    }
    
    // Fall back to localStorage or default
    return localStorage.getItem('preferredModel') || 'gpt-4o';
  };
  
  const selectedModel = getEffectiveModel();
  
  // Use the prop-based model selection
  const setSelectedModel = (model) => {
    localStorage.setItem('preferredModel', model); // Save as default for new conversations
    if (onModelChange) {
      onModelChange(model);
    }
  };
  const [isLoading, setIsLoading] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [streamingTokens, setStreamingTokens] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [enabledTools, setEnabledTools] = useState([]);
  const [availableTools, setAvailableTools] = useState(LEGACY_TOOLS);
  const [enableAutoTools, setEnableAutoTools] = useState(true);
  const [showManualToolsDropdown, setShowManualToolsDropdown] = useState(false);
  const [showSpaceDropdown, setShowSpaceDropdown] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [resendDropdownId, setResendDropdownId] = useState(null);
  const [burstMode, setBurstMode] = useState(false);
  const [selectedBurstGroup, setSelectedBurstGroup] = useState(null);
  const [burstResponses, setBurstResponses] = useState({});
  const [selectedBurstResponse, setSelectedBurstResponse] = useState({});
  const [extendedThinking, setExtendedThinking] = useState(false);
  const [thinkingMode, setThinkingMode] = useState('instant');
  const [thinkingBudget, setThinkingBudget] = useState(10000);
  const [use1MContext, setUse1MContext] = useState(false);
  const [showThinkingControl, setShowThinkingControl] = useState(false);
  const [openChats, setOpenChats] = useState([]);
  const [userCredits, setUserCredits] = useState(null);
  const [availableBurstModes, setAvailableBurstModes] = useState([]);
  const [loadingBurstModes, setLoadingBurstModes] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const [modelGroups, setModelGroups] = useState([]);
  const [topModelsConfig, setTopModelsConfig] = useState([]);
  const [showSlashCommands, setShowSlashCommands] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showOutOfCreditsModal, setShowOutOfCreditsModal] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 2000);
  const [modalImage, setModalImage] = useState(null);
  const showAIApps = propShowAIApps !== undefined ? propShowAIApps : false;
  const setShowAIApps = propSetShowAIApps || (() => {});
  const showContextSpaces = propShowContextSpaces !== undefined ? propShowContextSpaces : false;
  const setShowContextSpaces = propSetShowContextSpaces || (() => {});
  const [showAIAppsWidget, setShowAIAppsWidget] = useState(true);
  const [showContextSpacesWidget, setShowContextSpacesWidget] = useState(true);
  const [showWelcomeAIBlock, setShowWelcomeAIBlock] = useState(true);
  const [showWelcomeContextBlock, setShowWelcomeContextBlock] = useState(true);
  const [appOptions, setAppOptions] = useState({});
  const [showSystemPrompt, setShowSystemPrompt] = useState(true);
  const [editingSystemPrompt, setEditingSystemPrompt] = useState(false);
  const [tempSystemPrompt, setTempSystemPrompt] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [conversationApp, setConversationApp] = useState(null);
  
  // GPT-5 specific settings
  const [gpt5ReasoningEffort, setGpt5ReasoningEffort] = useState('minimal');
  const [gpt5Verbosity, setGpt5Verbosity] = useState('low');
  
  // Available AI Apps
  const availableApps = [
    {
      id: 'draft-composer',
      name: 'Draft Composer',
      description: 'Professional email and document drafting assistant',
      icon: Mail,
      color: 'bg-blue-500',
      systemPrompt: 'You are a professional writing assistant specializing in drafting emails, letters, and documents. Help create clear, professional, and well-structured written communications.'
    },
    {
      id: 'competitor-analyzer',
      name: 'Competitor Analyzer', 
      description: 'Analyze competitors and market positioning',
      icon: TrendingUp,
      color: 'bg-green-500',
      systemPrompt: 'You are a competitive analysis expert. Help analyze competitors, market positioning, and strategic insights to inform business decisions.'
    }
  ];
  
  const [showAIAppsDropdown, setShowAIAppsDropdown] = useState(false);
  const [showAppOptionModal, setShowAppOptionModal] = useState(null);
  
  // Voice-related state
  const [selectedVoice, setSelectedVoice] = useState('alloy');
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [autoPlayVoice, setAutoPlayVoice] = useState(false);
  const [showAudioConversation, setShowAudioConversation] = useState(false);
  const [showNativeConversation, setShowNativeConversation] = useState(false);
  const [showOpenAISDK, setShowOpenAISDK] = useState(false);
  const [showVoiceSelector, setShowVoiceSelector] = useState(false);
  const [showVoiceDropdown, setShowVoiceDropdown] = useState(false);
  const [collapsedMessages, setCollapsedMessages] = useState(new Set());
  const [showCitationPreview, setShowCitationPreview] = useState(false);
  const [selectedCitation, setSelectedCitation] = useState(null);
  const [showChatSettings, setShowChatSettings] = useState(false);
  const [showWelcomeOptionsDropdown, setShowWelcomeOptionsDropdown] = useState(false);
  const [showSlashCommandsDropdown, setShowSlashCommandsDropdown] = useState(false);
  
  const abortControllerRef = useRef(null);
  const chatSettingsRef = useRef(null);
  const voiceDropdownRef = useRef(null);
  const thinkingDropdownRef = useRef(null);
  const welcomeOptionsRef = useRef(null);
  
  // Click outside to close settings dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedElement = event.target;
      
      // Check if click is inside any of our dropdown containers
      const isInsideChatSettings = chatSettingsRef.current?.contains(clickedElement);
      const isInsideVoiceDropdown = voiceDropdownRef.current?.contains(clickedElement);
      const isInsideThinkingDropdown = thinkingDropdownRef.current?.contains(clickedElement);
      const isInsideWelcomeOptions = welcomeOptionsRef.current?.contains(clickedElement);
      
      // If click is outside all our containers, close everything
      if (!isInsideChatSettings && !isInsideVoiceDropdown && !isInsideThinkingDropdown && !isInsideWelcomeOptions) {
        setShowChatSettings(false);
        setShowVoiceDropdown(false);
        setShowThinkingControl(false);
        setShowWelcomeOptionsDropdown(false);
        setShowAIAppsDropdown(false);
        setShowManualToolsDropdown(false);
        setShowSlashCommandsDropdown(false);
      }
    };

    if (showChatSettings || showVoiceDropdown || showThinkingControl || showWelcomeOptionsDropdown || showAIAppsDropdown || showManualToolsDropdown || showSlashCommandsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showChatSettings, showVoiceDropdown, showThinkingControl]);

  // Initialize app options from conversation metadata
  useEffect(() => {
    if (isAppConversation && conversation?.metadata) {
      // Check if options are nested or at top level
      const options = conversation.metadata.options || {};
      
      // Extract options from metadata (they might be at top level)
      const extractedOptions = {};
      if (conversation.metadata.emailThread) extractedOptions.emailThread = conversation.metadata.emailThread;
      if (conversation.metadata.tone) extractedOptions.tone = conversation.metadata.tone;
      if (conversation.metadata.purpose) extractedOptions.purpose = conversation.metadata.purpose;
      if (conversation.metadata.multipleVersions) extractedOptions.multipleVersions = conversation.metadata.multipleVersions;
      
      const finalOptions = { ...extractedOptions, ...options };
      console.log('Initializing app options from metadata:', finalOptions);
      setAppOptions(finalOptions);
    }
  }, [isAppConversation, conversation?.metadata]);
  
  // Auto-enable tools for specific apps
  useEffect(() => {
    if (selectedApp?.id === 'competitor-analyzer' || conversationApp?.appId === 'competitor-analyzer') {
      // Automatically enable auto tools for competition analyzer
      setEnableAutoTools(true);
      // Also explicitly enable web_search tool
      setEnabledTools(prev => {
        if (!prev.includes('web_search')) {
          return [...prev, 'web_search'];
        }
        return prev;
      });
    }
  }, [selectedApp?.id, conversationApp?.appId]);
  
  // Check for pending app metadata when conversation is ready
  useEffect(() => {
    if (conversation?.id && !conversation.id.startsWith('temp-')) {
      const pendingMetadata = sessionStorage.getItem('pendingAppMetadata');
      if (pendingMetadata) {
        const metadata = JSON.parse(pendingMetadata);
        sessionStorage.removeItem('pendingAppMetadata');
        
        // Update conversation with app metadata
        onUpdateConversation(conversation.id, {
          metadata: metadata
        });
        
        // Mark as app conversation if it has app metadata
        if (metadata.appId) {
          window.location.pathname = `/apps/conversation/${conversation.id}`;
        }
      }
    }
  }, [conversation?.id, onUpdateConversation]);
  
  // Handle click outside for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside AI Apps dropdown
      if (showAIAppsDropdown && !event.target.closest('.ai-apps-dropdown-container')) {
        setShowAIAppsDropdown(false);
      }
      // Check if click is outside manual tools dropdown
      if (showManualToolsDropdown && !event.target.closest('.manual-tools-dropdown-container')) {
        setShowManualToolsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAIAppsDropdown, showManualToolsDropdown]);
  
  // Store auto-start state in a ref to handle it properly
  const autoStartHandledRef = useRef(false);
  
  // Handle auto-start for app conversations
  useEffect(() => {
    if (!conversation?.id || conversation?.id.startsWith('temp-') || autoStartHandledRef.current) return;
    
    // Check session storage for auto-start info
    const autoStartKey = `autoStart_${conversation.id}`;
    const autoStartData = sessionStorage.getItem(autoStartKey);
    
    console.log('Auto-start check:', {
      conversationId: conversation?.id,
      autoStartData,
      messagesLength: conversation?.messages?.length,
      handled: autoStartHandledRef.current,
      isAppConversation,
      appOptions
    });
    
    if (autoStartData && !conversation?.messages?.length && isAppConversation) {
      const { autoStart, initialPrompt } = JSON.parse(autoStartData);
      
      if (autoStart && !autoStartHandledRef.current) {
        console.log('Auto-starting app conversation with prompt:', initialPrompt);
        
        // Mark as handled
        autoStartHandledRef.current = true;
        
        // Don't remove from session storage yet - wait until message is actually sent
        // sessionStorage.removeItem(autoStartKey);
        
        // Wait for options to load, then send message
        const checkAndSend = () => {
          if (Object.keys(appOptions).length > 0 || conversation?.metadata?.emailThread) {
            console.log('Options loaded, sending auto-start message:', initialPrompt);
            console.log('Current appOptions:', appOptions);
            console.log('Conversation metadata:', conversation?.metadata);
            
            // Set the message in the input
            setMessage(initialPrompt);
            
            // Wait a bit for state to update, then click send
            setTimeout(() => {
              const sendButton = document.querySelector('[data-send-button]:not(:disabled)');
              if (sendButton) {
                console.log('Clicking send button with options:', appOptions);
                sendButton.click();
                // Remove from session storage after sending
                setTimeout(() => {
                  sessionStorage.removeItem(autoStartKey);
                }, 1000);
              } else {
                console.log('Send button not found or disabled');
              }
            }, 1000);
          } else {
            console.log('Waiting for options to load...', { appOptions, metadata: conversation?.metadata });
            setTimeout(checkAndSend, 200);
          }
        };
        
        setTimeout(checkAndSend, 1000);
      }
    }
  }, [conversation?.id, conversation?.messages?.length, isAppConversation, appOptions]);
  
  const streamingUpdateTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const { socket } = useSocket();
  const { canViewTeams, canViewBurstModes, loading, isAdmin } = usePermissions();
  const { isSuppleMode, isSuppleLight, isSuppleDark, appearanceMode } = useAppearance();
  
  // Memoize filtered models to avoid recalculating on every render
  const filteredModels = useMemo(() => {
    return Object.entries(models).filter(([provider, modelList]) => 
      Array.isArray(modelList) && modelList.length > 0
    );
  }, [models]);
  
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const editTextareaRef = useRef(null);
  const welcomeTextareaRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [isUserScrolledUp, setIsUserScrolledUp] = useState(false);
  const scrollTimeoutRef = useRef(null);
  const isScrollingProgrammatically = useRef(false);
  const dataLoadedRef = useRef(false);

  // Load available tools for current model
  const loadModelTools = useCallback(async (model) => {
    try {
      const provider = getProviderFromModel(model);
      const modelTools = await fetchModelTools(provider, model);
      const transformedTools = transformToolsForUI(modelTools.supportedTools);
      
      // Map string icon names to actual React components
      const toolsWithIcons = transformedTools.map(tool => ({
        ...tool,
        icon: ICON_MAP[tool.icon] || Zap // Default to Zap if icon not found
      }));
      
      setAvailableTools(toolsWithIcons);
      
      // Filter enabled tools to only include supported ones
      setEnabledTools(prev => 
        prev.filter(toolId => 
          modelTools.supportedToolIds.includes(toolId)
        )
      );
    } catch (error) {
      console.error('Failed to load model tools:', error);
      // Fallback to legacy tools
      setAvailableTools(LEGACY_TOOLS);
    }
  }, []);

  // Load tools when model changes
  useEffect(() => {
    if (selectedModel) {
      loadModelTools(selectedModel);
    }
  }, [selectedModel, loadModelTools]);
  
  // Don't auto-update model from conversation - let user control it

  // Throttled streaming message update to improve performance
  const throttledStreamingUpdate = useCallback((updateFn) => {
    if (streamingUpdateTimeoutRef.current) {
      clearTimeout(streamingUpdateTimeoutRef.current);
    }
    
    streamingUpdateTimeoutRef.current = setTimeout(() => {
      setStreamingMessage(updateFn);
    }, 50); // Update every 50ms instead of every token
  }, []);

  // Throttled token updates to reduce flickering
  const throttledTokenUpdate = useCallback((token) => {
    // Add token immediately for special tokens to prevent flickering
    if (token.startsWith('__THINKING_START__') ||
        token.startsWith('__THINKING_END__') ||
        token.startsWith('__PROGRESS_START__') ||
        token.startsWith('__WEB_SEARCH_START__') ||
        token.startsWith('__WEB_SEARCH_EXECUTING__') ||
        token.startsWith('__MCP_TOOL_START__') ||
        token.startsWith('__MCP_TOOL_EXECUTING__') ||
        token.startsWith('__MCP_TOOL_RESULT__') ||
        token.startsWith('__MCP_TOOL_ERROR__')) {
      setStreamingTokens(prev => [...prev, token]);
    } else {
      // Batch regular tokens to reduce updates
      setStreamingTokens(prev => [...prev, token]);
    }
  }, []);

  // Optimized input handler
  
  const handleMessageChange = useCallback((e) => {
    const value = e.target.value;
    
    // Update immediately for responsive typing
    setMessage(value);
  }, []);

  // Voice transcription handler
  const handleVoiceTranscription = useCallback((transcribedText) => {
    if (transcribedText.trim()) {
      // Add transcribed text to current message
      setMessage(prev => prev ? `${prev} ${transcribedText}` : transcribedText);
      
      // Focus textarea
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          // Move cursor to end
          textareaRef.current.setSelectionRange(
            textareaRef.current.value.length, 
            textareaRef.current.value.length
          );
        }
      }, 100);
    }
  }, []);


  const scrollToBottom = (force = false) => {
    // Check if user is scrolled up directly from DOM to avoid state sync issues
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20;
      
      // Only scroll if forced or user is already at bottom
      if (force || isAtBottom) {
        isScrollingProgrammatically.current = true;
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        
        // Reset the flag after scroll animation completes
        setTimeout(() => {
          isScrollingProgrammatically.current = false;
        }, 300);
      }
    }
  };

  // Track if user has scrolled up manually
  const handleScroll = () => {
    if (!messagesContainerRef.current || isScrollingProgrammatically.current) return;
    
    // Clear any pending scroll timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Debounce scroll detection to avoid flickering
    scrollTimeoutRef.current = setTimeout(() => {
      if (!messagesContainerRef.current) return;
      
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20; // Reduced threshold
      setIsUserScrolledUp(!isAtBottom);
    }, 50);
  };

  // Load teams, credits, and burst modes when component mounts
  useEffect(() => {
    // Prevent duplicate loading and infinite loops
    if (dataLoadedRef.current) {
      console.log('Data already loaded, skipping');
      return;
    }
    
    // Additional safety check
    if (!currentUser || loading) {
      console.log('Not ready to load data yet, user:', !!currentUser, 'loading:', loading);
      return;
    }
    
    // Debug logs commented out for performance
    // console.log('ChatInterface mounted, canViewTeams:', canViewTeams(), 'canViewBurstModes:', canViewBurstModes());
    // console.log('Current user:', currentUser);
    // console.log('Permissions context loading state:', loading);
    // console.log('Available models:', models);
    // console.log('Models metadata:', models._metadata);
    
    dataLoadedRef.current = true;
    
    loadUserCredits();
    
    // Always try to load burst modes - API will filter based on access
    loadBurstModes();
    
    // Load top models configuration
    loadTopModelsConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, loading]); // Only run when user and loading state are ready

  const loadBurstModes = async () => {
    try {
      setLoadingBurstModes(true);
      // console.log('Loading burst modes...');
      const burstModes = await fetchBurstModes();
      // console.log('Burst modes loaded:', burstModes);
      // console.log('Number of burst modes:', burstModes.length);
      setAvailableBurstModes(burstModes);
      setModelGroups(burstModes); // Use burst modes from database as model groups
      
      // Set first burst mode as default if available
      if (burstModes.length > 0 && !selectedBurstGroup) {
        setSelectedBurstGroup(burstModes[0]);
        // console.log('Set default burst group:', burstModes[0]);
      }
    } catch (error) {
      console.error('Failed to load burst modes:', error);
      setAvailableBurstModes([]);
      setModelGroups([]);
    } finally {
      setLoadingBurstModes(false);
    }
  };

  const loadTopModelsConfig = async () => {
    try {
      const topModelsResponse = await fetchTopModels();
      setTopModelsConfig(topModelsResponse.topModels || []);
    } catch (error) {
      console.error('Failed to load top models configuration:', error);
      // Use fallback defaults if API fails
      setTopModelsConfig([]);
    }
  };

  // Listen for incoming messages from users not in open chats


  const loadUserCredits = async () => {
    try {
      const credits = await fetchUserCredits();
      setUserCredits(credits);
    } catch (error) {
      console.error('Failed to load user credits:', error);
    }
  };

  const handleUpdateCredits = async ({ action, amount, newLimit, userId }) => {
    try {
      const targetUserId = userId || currentUser?.id;
      if (!targetUserId) return;

      let data = {};
      if (action === 'topup') {
        data.amount = amount;
      } else if (action === 'updateLimit') {
        data.monthlyCredits = newLimit;
      }

      await manageUserCredits(targetUserId, action, data);
      await loadUserCredits(); // Reload credits after update
    } catch (error) {
      console.error('Failed to update credits:', error);
      throw error;
    }
  };

  // Check if a model supports web search
  const supportsWebSearch = (modelId) => {
    const anthropicWebSearchModels = [
      'claude-4-opus',
      'claude-4-sonnet',
      'claude-3.7-sonnet',
      'claude-3-5-sonnet-20241022',
      'claude-3-5-haiku-20241022'
    ];
    return anthropicWebSearchModels.includes(modelId);
  };

  // Check if a model supports extended thinking
  const supportsExtendedThinking = (modelId) => {
    return modelId === 'claude-4-opus' || modelId === 'claude-3.7-sonnet';
  };

  // Check if model supports 1M context window (beta)
  const supports1MContext = (modelId) => {
    return modelId === 'claude-sonnet-4-5-20250929' || modelId === 'claude-4-sonnet';
  };

  // Check if current selection supports web search
  const currentSupportsWebSearch = () => {
    if (burstMode && selectedBurstGroup) {
      return selectedBurstGroup.models.some(m => supportsWebSearch(m));
    }
    return supportsWebSearch(selectedModel);
  };

  // Check if current selection supports extended thinking
  const currentSupportsExtendedThinking = () => {
    if (burstMode && selectedBurstGroup) {
      return selectedBurstGroup.models.some(m => supportsExtendedThinking(m));
    }
    return supportsExtendedThinking(selectedModel);
  };

  const handleSelectBurstResponse = (messageId, modelId) => {
    // Update the message to mark which response was selected
    const updatedMessages = (conversation?.messages || []).map(msg => 
      msg.id === messageId 
        ? { ...msg, selectedModel: modelId }
        : msg
    );
    
    onUpdateConversation(conversation.id, {
      messages: updatedMessages
    });
    
    setSelectedBurstResponse(prev => ({
      ...prev,
      [messageId]: modelId
    }));
  };

  // Deprecated: Image generation should go through the model's tool system
  // Keeping for reference but should not be called directly
  const handleImageGeneration_DEPRECATED = async (prompt) => {
    setMessage('');
    setIsLoading(true);

    // Add user message to conversation
    const newUserMessage = {
      id: Date.now().toString(),
      content: prompt,
      role: 'user',
      timestamp: new Date().toISOString(),
      model: selectedModel
    };

    const updatedMessages = [...(conversation.messages || []), newUserMessage];
    await onUpdateConversation(conversation.id, { 
      messages: updatedMessages,
      title: (!conversation?.messages || conversation.messages.length === 0) ? prompt.slice(0, 50) + (prompt.length > 50 ? '...' : '') : conversation.title
    });

    try {
      // Generate the image
      const result = await generateImage({
        prompt,
        size: '1024x1024',
        quality: 'standard',
        style: 'vivid',
        model: 'dall-e-3'
      });

      // Add assistant response with image
      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        content: `Here's the generated image:\n\n![Generated Image](${result.imageUrl})\n\n**Revised prompt:** ${result.revisedPrompt}\n\n**Cost:** $${result.cost.toFixed(3)}`,
        role: 'assistant',
        timestamp: new Date().toISOString(),
        model: 'dall-e-3',
        imageUrl: result.imageUrl,
        isImageGeneration: true,
        metadata: result.metadata
      };

      await onUpdateConversation(conversation.id, {
        messages: [...updatedMessages, assistantMessage]
      });

      // Refresh user credits
      if (typeof loadUserCredits === 'function') {
        loadUserCredits();
      }
    } catch (error) {
      console.error('Image generation error:', error);
      
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        content: `❌ **Error generating image**: ${error.message}`,
        role: 'assistant',
        timestamp: new Date().toISOString(),
        model: 'dall-e-3',
        isError: true,
        canRegenerate: true,
        originalUserMessage: prompt,
        originalModel: 'dall-e-3'
      };

      await onUpdateConversation(conversation.id, {
        messages: [...updatedMessages, errorMessage]
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBurstMessage = async (messageToSend, modelsToUse, upToMessageIndex) => {
    let conversationIdToUse = conversation.id;
    
    try {
      let messagesToUse = conversation.messages || [];
      
      if (upToMessageIndex !== null) {
        messagesToUse = messagesToUse.slice(0, upToMessageIndex + 1);
      }

      const burstMessageId = Date.now().toString();
      const newUserMessage = {
        id: burstMessageId,
        content: messageToSend,
        role: 'user',
        timestamp: new Date().toISOString(),
        isBurstMode: true,
        burstGroup: selectedBurstGroup.name,
        burstGroupId: selectedBurstGroup.id
      };

      const updatedMessages = [...messagesToUse, newUserMessage];
      const newTitle = (!conversation?.messages || conversation.messages.length === 0) ? messageToSend.slice(0, 50) + (messageToSend.length > 50 ? '...' : '') : conversation.title;
      
      const updateData = { 
        messages: updatedMessages,
        title: newTitle
      };
      
      // Include app metadata if this is the first message with a selected app
      if (conversationApp && (!conversation?.messages || conversation.messages.length === 0)) {
        updateData.metadata = conversationApp;
      }
      
      const newConversationId = await onUpdateConversation(conversation.id, updateData);
      
      conversationIdToUse = newConversationId || conversation.id;

      // Build conversation history
      const conversationHistory = updatedMessages
        .filter(msg => msg.role !== 'system')
        .slice(0, -1)
        .map(msg => {
          if (msg.isBurstResponse && msg.selectedModel && msg.burstResponses) {
            return {
              ...msg,
              content: msg.burstResponses[msg.selectedModel] || '',
              model: msg.selectedModel
            };
          }
          // Remove large data fields for RankBot messages when sending to AI
          if (msg.isRankBotResponse) {
            const { positionData, projectData, rankData, ...cleanMsg } = msg;
            return cleanMsg;
          }
          return msg;
        });

      // Create burst response placeholder
      const burstResponseMessage = {
        id: (parseInt(burstMessageId) + 1).toString(),
        role: 'assistant',
        timestamp: new Date().toISOString(),
        isBurstResponse: true,
        burstResponses: {},
        userMessageId: burstMessageId,
        isStreaming: true
      };

      onUpdateConversation(conversationIdToUse, {
        messages: [...updatedMessages, burstResponseMessage]
      });

      // Send requests to all models in parallel
      const responses = {};
      const promises = modelsToUse.map(async (modelId) => {
        try {
          let modelResponse = '';
          // Build enhanced message with app options
          let enhancedMessage = messageToSend;
          if ((isAppConversation || selectedApp) && Object.keys(appOptions).length > 0) {
            const optionsContext = Object.entries(appOptions)
              .filter(([key, value]) => value && value !== '')
              .map(([key, value]) => {
                // Format the key nicely
                const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
                return `${formattedKey}: ${value}`;
              })
              .join('\n');
            
            if (optionsContext) {
              enhancedMessage = `[Context]\n${optionsContext}\n\n[Request]\n${messageToSend}`;
            }
          }

          // Add Context Space context if available
          if (contextSpaceContext && selectedContextSpaceId) {
            let contextContent = `# ${contextSpaceContext.name} Context Space\n`;
            
            if (contextSpaceContext.description) {
              contextContent += `${contextSpaceContext.description}\n\n`;
            }

            // Add resources (concise version)
            if (contextSpaceContext.resources && contextSpaceContext.resources.length > 0) {
              contextContent += `## Available Resources (${contextSpaceContext.resources.length}):\n`;
              contextSpaceContext.resources.slice(0, 5).forEach(resource => {
                  contextContent += `• **${resource.title}**`;
                if (resource.description) {
                  contextContent += ` - ${resource.description}`;
                }
                contextContent += `\n`;
                // Add the full resource content
                if (resource.content) {
                  // Limit content to prevent context overflow (first 2000 characters)
                  const truncatedContent = resource.content.length > 2000 
                    ? resource.content.substring(0, 2000) + '...'
                    : resource.content;
                  contextContent += `${truncatedContent}\n\n`;
                }
              });
              if (contextSpaceContext.resources.length > 5) {
                contextContent += `... and ${contextSpaceContext.resources.length - 5} more resources\n`;
              }
              contextContent += `\n`;
            }

            // Add conversation history from the Context Space (concise)
            if (contextSpaceContext.conversationHistory && contextSpaceContext.conversationHistory.length > 0) {
              contextContent += `## Related Conversations (${contextSpaceContext.conversationHistory.length}):\n`;
              contextSpaceContext.conversationHistory.slice(0, 3).forEach(conv => {
                contextContent += `• **${conv.title}**`;
                if (conv.messages && conv.messages.length > 0) {
                  const lastMsg = conv.messages[conv.messages.length - 1];
                  const preview = lastMsg.content.substring(0, 100) + (lastMsg.content.length > 100 ? '...' : '');
                  contextContent += ` - Latest: ${preview}`;
                }
                contextContent += `\n`;
              });
              if (contextSpaceContext.conversationHistory.length > 3) {
                contextContent += `... and ${contextSpaceContext.conversationHistory.length - 3} more conversations\n`;
              }
              contextContent += `\n`;
            }

            // Add citation instructions
            const citationInstructions = `\n## Citation Instructions:\nWhen referencing any information from the above resources or conversations, always include inline citations in this format:\n- For resources: [Source: ResourceTitle]\n- For conversations: [Source: ConversationTitle]\nPlace citations immediately after the relevant text. The system will automatically convert these to numbered inline citations with a references section at the bottom.\n\n`;

            // Combine with existing context or replace message
            if (enhancedMessage.includes('[Context]')) {
              enhancedMessage = enhancedMessage.replace('[Context]\n', `[Context]\n${contextContent}${citationInstructions}`);
            } else {
              enhancedMessage = `[Context Space: ${contextSpaceContext.name}]\n${contextContent}${citationInstructions}[Request]\n${messageToSend}`;
            }
          }

          await sendChatMessage({
            message: enhancedMessage,
            userMessage: messageToSend, // Clean message for storage
            model: modelId,
            conversationHistory: conversationHistory,
            enabledTools,
            enableAutoTools,
            extendedThinking: extendedThinking && supportsExtendedThinking(modelId),
            thinkingMode,
            thinkingBudget,
            use1MContext: use1MContext && supports1MContext(modelId),
            conversationId: conversationIdToUse,
            systemPrompt: isAppConversation ? conversation?.metadata?.systemPrompt : conversationApp?.systemPrompt,
            abortSignal: abortControllerRef.current?.signal,
            onToken: (token) => {
              modelResponse += token;
              setBurstResponses(prev => ({
                ...prev,
                [`${burstMessageId}-${modelId}`]: modelResponse
              }));
            },
            onComplete: () => {
              responses[modelId] = modelResponse;
            },
            onError: (error) => {
              responses[modelId] = `Error: ${error.message}`;
            }
          });
        } catch (error) {
          responses[modelId] = `Error: ${error.message}`;
        }
      });

      await Promise.all(promises);

      // Update the burst response message with all responses
      const finalMessages = [...updatedMessages, burstResponseMessage];
      const updatedMessagesWithResponses = finalMessages.map(msg => 
        msg.id === burstResponseMessage.id 
          ? { ...msg, burstResponses: responses, isStreaming: false }
          : msg
      );
      
      onUpdateConversation(conversationIdToUse, {
        messages: updatedMessagesWithResponses
      });

      // Clear burst responses
      setTimeout(() => {
        setBurstResponses({});
        setIsLoading(false);
        setIsStreaming(false);
      }, 100);
    } catch (error) {
      console.error('Error in handleBurstMessage:', error);
      setIsLoading(false);
      setIsStreaming(false);
      setBurstResponses({});
      
      onUpdateConversation(conversationIdToUse || conversation.id, {
        messages: [...(conversation?.messages || []), {
          id: Date.now().toString(),
          content: `Error: ${error.message}`,
          role: 'assistant',
          timestamp: new Date().toISOString(),
          isError: true
        }]
      });
    }
  };

  const toggleTool = (toolId) => {
    setEnabledTools(prev => {
      if (prev.includes(toolId)) {
        return prev.filter(id => id !== toolId);
      } else {
        return [...prev, toolId];
      }
    });
  };

  // Auto-scroll only when new messages are added (not during streaming) and user is at bottom
  useEffect(() => {
    if (!isStreaming && conversation?.messages?.length > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        scrollToBottom(); // This will check if user is at bottom before scrolling
      }, 50);
    }
  }, [conversation?.messages?.length, isStreaming]);

  // For streaming, throttle scroll updates and only if user is at bottom
  useEffect(() => {
    if (isStreaming && streamingMessage) {
      // Check scroll position directly from DOM instead of relying on state
      if (messagesContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20;
        
        // Only auto-scroll if user is at the bottom
        if (isAtBottom) {
          // Throttle scroll updates during streaming to reduce flickering
          const timeoutId = setTimeout(() => {
            scrollToBottom();
          }, 100);
          
          return () => clearTimeout(timeoutId);
        }
      }
    }
  }, [streamingMessage, isStreaming]);

  // Add scroll event listener
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        container.removeEventListener('scroll', handleScroll);
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      };
    }
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (streamingUpdateTimeoutRef.current) {
        clearTimeout(streamingUpdateTimeoutRef.current);
      }
    };
  }, []);

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const isLarge = window.innerWidth >= 1024;
      setIsLargeScreen(isLarge);
      
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Simple textarea resizing with flex layout handling upward growth
  useEffect(() => {
    let rafId;
    const resizeTextarea = () => {
      if (textareaRef.current) {
        const textarea = textareaRef.current;
        const minHeight = 44;
        const maxHeight = 88;

        // Reset height to calculate scroll height
        textarea.style.height = 'auto';
        const scrollHeight = textarea.scrollHeight;
        const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);

        // Set the calculated height
        textarea.style.height = `${newHeight}px`;
      }
      if (welcomeTextareaRef.current) {
        welcomeTextareaRef.current.style.height = 'auto';
        const scrollHeight = welcomeTextareaRef.current.scrollHeight;
        const maxHeight = 300;
        welcomeTextareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
      }
    };
    
    // Use requestAnimationFrame for smooth resizing
    rafId = requestAnimationFrame(resizeTextarea);
    
    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [message]);

  useEffect(() => {
    if (editTextareaRef.current && editingMessageId) {
      editTextareaRef.current.style.height = 'auto';
      editTextareaRef.current.style.height = `${editTextareaRef.current.scrollHeight}px`;
    }
  }, [editingContent, editingMessageId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is inside any dropdown
      const isInsideResendDropdown = event.target.closest('.resend-dropdown-container');
      
      if (resendDropdownId && !isInsideResendDropdown) {
        setResendDropdownId(null);
      }

      // Check if click is outside space dropdown
      const spaceDropdown = event.target.closest('[data-space-dropdown]');
      if (showSpaceDropdown && !spaceDropdown) {
        setShowSpaceDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [resendDropdownId, showSpaceDropdown]);

  // File upload handler (handles single file or array of files)
  const handleFileUpload = async (files) => {
    try {
      const fileArray = Array.isArray(files) ? files : [files];
      const uploadResults = [];
      
      for (const file of fileArray) {
        const result = await uploadFile(file);
        
        // Add file to attached files list
        const fileInfo = {
          fileId: result.fileId,
          name: file.name,
          size: file.size,
          type: file.type,
          url: result.url,
          isImage: file.type.startsWith('image/'),
          isDocument: !file.type.startsWith('image/'),
          uploadedAt: new Date().toISOString(),
          filename: result.filename
        };

        setAttachedFiles(prev => [...prev, fileInfo]);
        uploadResults.push(result);
      }
      
      return uploadResults.length === 1 ? uploadResults[0] : uploadResults;
    } catch (error) {
      console.error('File upload failed:', error);
      throw error;
    }
  };

  // Remove attached file
  const removeAttachedFile = (fileId) => {
    setAttachedFiles(prev => prev.filter(file => file.fileId !== fileId));
  };

  // Handle direct file selection from input
  const handleDirectFileSelection = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFileUpload(files);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  // Trigger file input click
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Check if current model supports vision/file processing
  const currentModelSupportsVision = () => {
    const visionModels = [
      'gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-4-vision-preview',
      'claude-3-opus', 'claude-3-sonnet', 'claude-3-haiku',
      'claude-3-5-sonnet', 'claude-3-5-haiku',
      'claude-sonnet-4', 'claude-opus-4',
      'gemini-1.5-pro', 'gemini-1.5-flash', 'gemini-2.0-flash', 'gemini-2.5-pro'
    ];
    
    return visionModels.some(model => selectedModel.toLowerCase().includes(model.toLowerCase()));
  };

  // Global drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setDragCounter(prev => prev + 1);
    
    // Check if we're dragging files (not text)
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      // Check if any of the items are files
      const hasFiles = Array.from(e.dataTransfer.items).some(item => item.kind === 'file');
      
      // Also check types - if it includes Files, it's likely a file drag
      const hasFileType = e.dataTransfer.types.includes('Files');
      
      // Only show drag overlay for actual files, not text
      if (hasFiles || hasFileType) {
        setIsDragging(true);
      }
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setDragCounter(prev => prev - 1);
    if (dragCounter === 1) {
      setIsDragging(false);
    }
  }, [dragCounter]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only set dropEffect for files, not text
    const hasFileType = e.dataTransfer.types.includes('Files');
    if (hasFileType) {
      e.dataTransfer.dropEffect = currentModelSupportsVision() ? 'copy' : 'none';
    } else {
      e.dataTransfer.dropEffect = 'none';
    }
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsDragging(false);
    setDragCounter(0);
    
    // Only handle file drops, ignore text drops
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      if (currentModelSupportsVision()) {
        try {
          await handleFileUpload(files);
        } catch (error) {
          console.error('Drag and drop file upload failed:', error);
        }
      } else {
        // Show a message asking to switch models - we'll just not upload for now
        // The UI overlay will show the message
        console.log('Model does not support vision - showing message to user');
      }
    }
    // If it's text being dropped, just ignore it completely
  }, []);


  const handleSendMessage = async (customMessage = null, customModel = null, upToMessageIndex = null, customMessagesToUse = null) => {
    const messageToSend = customMessage || message.trim();
    if (!messageToSend || isLoading) return;

    // If no conversation exists, create one and queue the message
    if (!conversation && onNewConversation) {
      console.log('No conversation exists, creating one and queuing message:', messageToSend);
      setPendingMessage(messageToSend);
      await onNewConversation();
      return;
    }

    // If we still don't have a conversation, return
    if (!conversation) {
      console.log('No conversation available');
      return;
    }
    
    // Check if user has credits remaining
    if (userCredits && userCredits.creditsRemaining <= 0) {
      setShowOutOfCreditsModal(true);
      return;
    }
    
    // Check if the last message is a burst response without a selected model
    const lastMessage = conversation.messages?.[conversation.messages.length - 1];
    if (lastMessage?.isBurstResponse && !lastMessage.selectedModel) {
      alert('Please select a response from the burst mode before continuing the conversation.');
      return;
    }

    // Image generation should only happen through the tools system
    // Removed automatic image generation detection to prevent unwanted API calls

    if (!customMessage) {
      setMessage('');
      setAttachedFiles([]); // Clear attached files after sending
    }
    setIsLoading(true);
    setIsStreaming(true);
    setStreamingMessage('');
    setStreamingTokens([]);
    setEditingMessageId(null);

    // Handle burst mode
    if (burstMode && selectedBurstGroup && selectedBurstGroup.models && !customModel) {
      const modelsToUse = selectedBurstGroup.models;
      if (modelsToUse.length === 0) {
        alert('The selected burst group has no models.');
        setIsLoading(false);
        setIsStreaming(false);
        return;
      }
      return handleBurstMessage(messageToSend, modelsToUse, upToMessageIndex);
    }

    const modelToUse = customModel || selectedModel;
    // console.log('[Send Message] Using model:', modelToUse, '(custom:', customModel, ', selected:', selectedModel, ')');
    let conversationIdToUse = conversation.id;

    // Use custom messages if provided (for regenerate), otherwise use conversation messages
    let messagesToUse = customMessagesToUse !== null ? customMessagesToUse : (conversation.messages || []);

    if (upToMessageIndex !== null && customMessagesToUse === null) {
      messagesToUse = messagesToUse.slice(0, upToMessageIndex + 1);
    }

    const newUserMessage = {
      id: Date.now().toString(),
      content: messageToSend,
      role: 'user',
      timestamp: new Date().toISOString(),
      model: modelToUse,
      attachments: attachedFiles.length > 0 ? [...attachedFiles] : undefined,
      appOptions: isAppConversation && Object.keys(appOptions).length > 0 ? appOptions : undefined
    };

    const updatedMessages = [...messagesToUse, newUserMessage];
    const updateData = { 
      messages: updatedMessages,
      title: (!conversation?.messages || conversation.messages.length === 0) ? messageToSend.slice(0, 50) + (messageToSend.length > 50 ? '...' : '') : conversation.title
    };
    
    // Include app metadata if this is the first message with a selected app
    if (conversationApp && (!conversation?.messages || conversation.messages.length === 0)) {
      updateData.metadata = conversationApp;
    }
    
    const newConversationId = await onUpdateConversation(conversation.id, updateData);
    
    conversationIdToUse = newConversationId || conversation.id;

    const conversationHistory = updatedMessages
      .filter(msg => msg.role !== 'system')
      .slice(0, -1)
      .map(msg => {
        if (msg.isBurstResponse && msg.selectedModel && msg.burstResponses) {
          return {
            ...msg,
            content: msg.burstResponses[msg.selectedModel] || '',
            model: msg.selectedModel
          };
        }
        // Remove large data fields for RankBot messages when sending to AI
        if (msg.isRankBotResponse) {
          const { positionData, projectData, rankData, ...cleanMsg } = msg;
          return cleanMsg;
        }
        return msg;
      });

    try {
      let assistantResponse = '';

      // Create abort controller for this request
      abortControllerRef.current = new AbortController();

      // Build enhanced message with app options if it's an app conversation
      let enhancedMessage = messageToSend;
      if ((isAppConversation || selectedApp) && Object.keys(appOptions).length > 0) {
        console.log('App conversation detected, building enhanced message');
        console.log('Current appOptions:', appOptions);
        console.log('Options entries:', Object.entries(appOptions));
        
        const optionsContext = Object.entries(appOptions)
          .filter(([key, value]) => value && value !== '')
          .map(([key, value]) => {
            // Format the key nicely
            const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
            // Limit value length to prevent huge payloads
            const limitedValue = typeof value === 'string' && value.length > 1000 
              ? value.substring(0, 1000) + '...' 
              : value;
            return `${formattedKey}: ${limitedValue}`;
          })
          .join('\n');
        
        console.log('Options context:', optionsContext);
        
        if (optionsContext) {
          enhancedMessage = `[Context]\n${optionsContext}\n\n[Request]\n${messageToSend}`;
          console.log('Final enhanced message:', enhancedMessage);
        }
      } else {
        console.log('Not enhancing message:', { isAppConversation, selectedApp: !!selectedApp, appOptionsLength: Object.keys(appOptions).length });
      }
      
      console.log('Sending message:', {
        isAppConversation,
        conversationId: conversationIdToUse,
        systemPrompt: conversation?.metadata?.systemPrompt,
        model: modelToUse,
        enhancedMessage
      });

      // Add Context Space context if available
      if (contextSpaceContext && selectedContextSpaceId) {
        let contextContent = `# ${contextSpaceContext.name} Context Space\n`;
        
        if (contextSpaceContext.description) {
          contextContent += `${contextSpaceContext.description}\n\n`;
        }

        // Add resources (concise version)
        if (contextSpaceContext.resources && contextSpaceContext.resources.length > 0) {
          contextContent += `## Available Resources (${contextSpaceContext.resources.length}):\n`;
          contextSpaceContext.resources.slice(0, 5).forEach(resource => {
            contextContent += `• **${resource.title}**`;
            if (resource.description) {
              contextContent += ` - ${resource.description}`;
            }
            contextContent += `\n`;
            // Add the full resource content
            if (resource.content) {
              // Limit content to prevent context overflow (first 2000 characters)
              const truncatedContent = resource.content.length > 2000 
                ? resource.content.substring(0, 2000) + '...'
                : resource.content;
              contextContent += `${truncatedContent}\n\n`;
            }
          });
          if (contextSpaceContext.resources.length > 5) {
            contextContent += `... and ${contextSpaceContext.resources.length - 5} more resources\n`;
          }
          contextContent += `\n`;
        }

        // Add conversation history from the Context Space (concise)
        if (contextSpaceContext.conversationHistory && contextSpaceContext.conversationHistory.length > 0) {
          contextContent += `## Related Conversations (${contextSpaceContext.conversationHistory.length}):\n`;
          contextSpaceContext.conversationHistory.slice(0, 3).forEach(conv => {
            contextContent += `• **${conv.title}**`;
            if (conv.messages && conv.messages.length > 0) {
              const lastMsg = conv.messages[conv.messages.length - 1];
              const preview = lastMsg.content.substring(0, 100) + (lastMsg.content.length > 100 ? '...' : '');
              contextContent += ` - Latest: ${preview}`;
            }
            contextContent += `\n`;
          });
          if (contextSpaceContext.conversationHistory.length > 3) {
            contextContent += `... and ${contextSpaceContext.conversationHistory.length - 3} more conversations\n`;
          }
          contextContent += `\n`;
        }

        // Add citation instructions
        const citationInstructions = `\n## Citation Instructions:\nWhen referencing any information from the above resources or conversations, always include inline citations in this format:\n- For resources: [Source: ResourceTitle]\n- For conversations: [Source: ConversationTitle]\nPlace citations immediately after the relevant text. The system will automatically convert these to numbered inline citations with a references section at the bottom.\n\n`;

        // Combine with existing context or replace message
        if (enhancedMessage.includes('[Context]')) {
          enhancedMessage = enhancedMessage.replace('[Context]\n', `[Context]\n${contextContent}${citationInstructions}`);
        } else {
          enhancedMessage = `[Context Space: ${contextSpaceContext.name}]\n${contextContent}${citationInstructions}[Request]\n${messageToSend}`;
        }
      }

      await sendChatMessage({
        message: enhancedMessage,
        userMessage: messageToSend, // Clean message for storage
        model: modelToUse,
        conversationHistory: conversationHistory,
        enabledTools,
        enableAutoTools,
        extendedThinking: extendedThinking && supportsExtendedThinking(modelToUse),
        thinkingMode,
        thinkingBudget,
        use1MContext: use1MContext && supports1MContext(modelToUse),
        gpt5ReasoningEffort, // Add GPT-5 reasoning effort
        gpt5Verbosity, // Add GPT-5 verbosity
        conversationId: conversationIdToUse,
        systemPrompt: isAppConversation ? conversation?.metadata?.systemPrompt : conversationApp?.systemPrompt,
        attachedFiles: attachedFiles.map(file => file.fileId), // Send array of file IDs
        selectedMCPIds: selectedMCPs, // Pass selected MCP server IDs
        abortSignal: abortControllerRef.current.signal,
        onToken: (token) => {
          // Add token to the streaming tokens array with throttling
          throttledTokenUpdate(token);
          // Handle special tokens (these need immediate updates)
          if (token.startsWith('__PROGRESS_START__')) {
            const progressText = token.replace('__PROGRESS_START__', '');
            setStreamingMessage(prev => ({
              content: assistantResponse,
              progressStatus: 'starting',
              progressText: progressText,
              showProgress: true
            }));
            return; // Don't add to assistantResponse
          } else if (token.startsWith('__TOOL_AUTO__')) {
            const toolText = token.replace('__TOOL_AUTO__', '');
            setStreamingMessage(prev => ({
              ...prev,
              progressStatus: 'tool_auto',
              progressText: toolText,
              showProgress: true
            }));
            return; // Don't add to assistantResponse
          } else if (token.startsWith('__STREAM_START__')) {
            const streamText = token.replace('__STREAM_START__', '');
            setStreamingMessage(prev => ({
              ...prev,
              progressStatus: 'streaming',
              progressText: streamText,
              showProgress: true
            }));
            return; // Don't add to assistantResponse
          } else if (token.startsWith('__TOOLS_READY__')) {
            const toolsText = token.replace('__TOOLS_READY__', '');
            setStreamingMessage(prev => ({
              ...prev,
              progressStatus: 'tools_ready',
              progressText: toolsText,
              showProgress: true
            }));
            return; // Don't add to assistantResponse
          } else if (token.startsWith('__WEB_SEARCH_START__')) {
            setStreamingMessage(prev => ({
              content: assistantResponse,
              webSearchActive: true,
              webSearchStatus: 'starting',
              showProgress: false
            }));
            return; // Don't add to assistantResponse
          } else if (token.startsWith('__WEB_SEARCH_QUERY__')) {
            const query = token.replace('__WEB_SEARCH_QUERY__', '');
            setStreamingMessage(prev => ({
              ...prev,
              webSearchQuery: (prev.webSearchQuery || '') + query
            }));
            return; // Don't add to assistantResponse
          } else if (token.startsWith('__WEB_SEARCH_EXECUTING__')) {
            setStreamingMessage(prev => ({
              ...prev,
              webSearchStatus: 'executing'
            }));
            return; // Don't add to assistantResponse
          } else if (token.startsWith('__THINKING_START__')) {
            setStreamingMessage(prev => ({
              ...prev,
              isThinking: true,
              thinkingContent: '',
              showProgress: false
            }));
            return; // Don't add to assistantResponse
          } else if (token.startsWith('__THINKING__')) {
            const thinkingText = token.replace('__THINKING__', '');
            setStreamingMessage(prev => ({
              ...prev,
              thinkingContent: (prev.thinkingContent || '') + thinkingText
            }));
            return; // Don't add to assistantResponse
          } else if (token.startsWith('__THINKING_END__')) {
            setStreamingMessage(prev => ({
              ...prev,
              isThinking: false
            }));
            return; // Don't add to assistantResponse
          } else if (token.startsWith('__MCP_TOOL_START__') ||
                     token.startsWith('__MCP_TOOL_EXECUTING__') ||
                     token.startsWith('__MCP_TOOL_RESULT__') ||
                     token.startsWith('__MCP_TOOL_ERROR__')) {
            // MCP tool tokens - add to assistantResponse with newlines so they persist after streaming
            // The newlines match the server format and allow StreamingMessageRenderer to parse them
            assistantResponse += '\n' + token + '\n';
            throttledStreamingUpdate(prev => {
              if (typeof prev === 'object' && prev !== null) {
                return { ...prev, content: assistantResponse };
              }
              return assistantResponse;
            });
            return;
          } else {
            // Regular content - use throttled updates for better performance
            assistantResponse += token;
            throttledStreamingUpdate(prev => {
              if (typeof prev === 'object' && prev !== null) {
                // Hide progress indicators when actual content starts
                const updatedState = { ...prev, content: assistantResponse, showProgress: false };

                if (prev.webSearchActive && !token.startsWith('__')) {
                  updatedState.webSearchStatus = 'complete';
                }
                return updatedState;
              }
              return assistantResponse;
            });
          }
        },
        onComplete: (completionData) => {
          const metadata = typeof streamingMessage === 'object' && streamingMessage !== null
            ? streamingMessage
            : {};
          
          
          const assistantMessage = {
            id: (Date.now() + 1).toString(),
            content: assistantResponse,
            role: 'assistant',
            timestamp: new Date().toISOString(),
            model: modelToUse,
            ...(metadata.webSearchActive && {
              webSearchActive: metadata.webSearchActive,
              webSearchStatus: metadata.webSearchStatus,
              webSearchQuery: metadata.webSearchQuery
            }),
            ...(metadata.thinkingContent && {
              thinkingContent: metadata.thinkingContent,
              isThinking: false
            }),
            // Add token and cost data if available
            ...(completionData && {
              tokensUsed: completionData.tokensUsed,
              inputTokens: completionData.inputTokens,
              outputTokens: completionData.outputTokens,
              cost: completionData.cost,
              costBreakdown: completionData.costBreakdown
            }),
          };

          onUpdateConversation(conversationIdToUse, {
            messages: [...updatedMessages, assistantMessage]
          });
          

          // Clear any pending streaming updates
          if (streamingUpdateTimeoutRef.current) {
            clearTimeout(streamingUpdateTimeoutRef.current);
          }
          
          setStreamingMessage('');
      setStreamingTokens([]);
          setStreamingTokens([]);
          setIsStreaming(false);
          setIsLoading(false);
          
          // Refresh user credits after message
          loadUserCredits();
        },
        onError: (error) => {
          console.error('Chat error:', error);
          
          // Don't show error for aborted requests
          if (error.name === 'AbortError') {
            // Clear any pending streaming updates
            if (streamingUpdateTimeoutRef.current) {
              clearTimeout(streamingUpdateTimeoutRef.current);
            }
            setStreamingMessage('');
      setStreamingTokens([]);
            setIsStreaming(false);
            setIsLoading(false);
            return;
          }

          const errorMessage = {
            id: (Date.now() + 1).toString(),
            content: `Error: ${error.message}`,
            role: 'assistant',
            timestamp: new Date().toISOString(),
            model: modelToUse,
            isError: true
          };

          onUpdateConversation(conversation.id, {
            messages: [...updatedMessages, errorMessage]
          });

          // Clear any pending streaming updates
          if (streamingUpdateTimeoutRef.current) {
            clearTimeout(streamingUpdateTimeoutRef.current);
          }
          
          setStreamingMessage('');
      setStreamingTokens([]);
          setStreamingTokens([]);
          setIsStreaming(false);
          setIsLoading(false);
        }
      });
    } catch (error) {
      // Don't show error for aborted requests
      if (error.name !== 'AbortError') {
        console.error('Send message error:', error);
        
        // Create an error message to display in the conversation
        const errorMessage = {
          id: (Date.now() + 1).toString(),
          content: `❌ **Error**: ${error.message || 'An unexpected error occurred while generating the response.'}`,
          role: 'assistant',
          timestamp: new Date().toISOString(),
          model: modelToUse,
          isError: true, // Mark this as an error message
          canRegenerate: true, // Allow regeneration
          originalUserMessage: messageToSend, // Store the original message for regeneration
          originalModel: modelToUse
        };

        // Add error message to conversation
        const errorMessages = [...updatedMessages, errorMessage];
        await onUpdateConversation(conversationIdToUse, { 
          messages: errorMessages
        });
      }
      // Clear any pending streaming updates
      if (streamingUpdateTimeoutRef.current) {
        clearTimeout(streamingUpdateTimeoutRef.current);
      }
      setIsLoading(false);
      setIsStreaming(false);
      setStreamingMessage('');
      setStreamingTokens([]);
    } finally {
      // Clean up abort controller
      abortControllerRef.current = null;
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isStreaming) {
        handleStopResponse();
      } else {
        handleSendMessage();
      }
    }
  };

  const handleStopResponse = () => {
    // Abort the current request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Stop streaming state
    setIsStreaming(false);
    setIsLoading(false);

    // If there's a streaming message, save it as the final response
    if (streamingMessage) {
      const finalContent = typeof streamingMessage === 'object' 
        ? streamingMessage.content || ''
        : streamingMessage;

      if (finalContent.trim()) {
        const assistantMessage = {
          id: (Date.now() + 1).toString(),
          content: finalContent,
          role: 'assistant',
          timestamp: new Date().toISOString(),
          model: selectedModel,
          stopped: true // Mark as stopped by user
        };

        const updatedMessages = [...(conversation.messages || []), assistantMessage];
        onUpdateConversation(conversation.id, {
          messages: updatedMessages
        });
      }
    }

    // Clear streaming message and any pending updates
    if (streamingUpdateTimeoutRef.current) {
      clearTimeout(streamingUpdateTimeoutRef.current);
    }
    setStreamingMessage('');
  };

  const handleRegenerateMessage = async (errorMessage) => {
    if (!errorMessage.canRegenerate) return;

    // Find the index of the error message
    const errorIndex = conversation.messages.findIndex(msg => msg.id === errorMessage.id);

    // Remove the error message AND all messages after it
    const messagesUpToError = conversation.messages.slice(0, errorIndex);
    await onUpdateConversation(conversation.id, {
      messages: messagesUpToError
    });

    // Regenerate with the original message and model, passing truncated messages
    handleSendMessage(errorMessage.originalUserMessage, errorMessage.originalModel, null, messagesUpToError);
  };

  const handleSlashCommand = (commandId, data) => {
    console.log('Executing slash command:', commandId, data);
    
    // Create a special message for slash commands
    const commandMessage = {
      id: Date.now().toString(),
      content: `/${commandId}`,
      role: 'user',
      timestamp: new Date().toISOString(),
      isSlashCommand: true,
      commandId,
      commandData: data
    };

    // Create the bot response message with RankBot component
    const botMessage = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      timestamp: new Date().toISOString(),
      model: 'RankBot',
      isRankBotResponse: true,
      commandId,
      commandData: data,
      content: `Processing ${commandId} command for ${data.domain}...`
    };

    const updatedMessages = [...(conversation.messages || []), commandMessage, botMessage];
    
    onUpdateConversation(conversation.id, {
      messages: updatedMessages,
      title: conversation.title || `${commandId} analysis for ${data.domain}`
    });

    setIsLoading(false);
    setIsStreaming(false);
  };

  const handleRankBotComplete = (messageId, rankData) => {
    // Update the message with completed rank data
    const updatedMessages = (conversation?.messages || []).map(msg => 
      msg.id === messageId 
        ? { 
            ...msg, 
            projectData: rankData.projectData,
            positionData: rankData.positionData,
            isComplete: true
          }
        : msg
    );

    onUpdateConversation(conversation.id, {
      messages: updatedMessages
    });
  };

  const handleShareConversation = () => {
    const shareableLink = `${window.location.origin}/conversation/${conversation.id}`;
    navigator.clipboard.writeText(shareableLink);
    alert('Conversation link copied to clipboard!');
  };

  const toggleMessageCollapse = (messageId) => {
    setCollapsedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const handleCitationClick = (citationTitle, contextSpaceId) => {
    setSelectedCitation({ title: citationTitle, contextSpaceId });
    setShowCitationPreview(true);
  };

  const handleSelectContextSpace = (contextSpace) => {
    // Navigate to the context space
    window.location.href = `/context-space/${contextSpace.id}`;
  };

  const handleOpenContextSpacesFullView = () => {
    // Open the Context Spaces page
    setShowContextSpaces(true);
  };

  const handleSelectAIApp = async (app) => {
    // Close the AI Apps modal
    setShowAIApps(false);
    
    // For AI Apps with metadata (from AIAppsWidget), handle directly
    if (app.metadata && app.id) {
      // Set the selected app
      setSelectedApp(app);
      
      // Set the app options
      setAppOptions(app.metadata.options || {});
      
      // Store app data for the conversation
      const appMetadata = {
        appId: app.id,
        appName: app.name,
        systemPrompt: app.systemPrompt,
        ...app.metadata
      };
      
      // Store in state instead of trying to create conversation immediately
      setConversationApp(appMetadata);
      
      // Focus the textarea
      setTimeout(() => {
        const textarea = welcomeTextareaRef.current || textareaRef.current;
        if (textarea) {
          textarea.focus();
        }
      }, 200);
    } else {
      // Legacy behavior for basic AI assistants from AIApps modal
      // Create a new conversation if we have messages or no conversation
      if (!conversation || conversation.messages?.length > 0) {
        if (onNewConversation) {
          await onNewConversation();
        }
      }
      
      // Set the system prompt as message and send it
      setTimeout(() => {
        setMessage(app.systemPrompt);
        handleSendMessage(app.systemPrompt);
      }, 100);
    }
  };


  const handleCloseChat = (memberId) => {
    setOpenChats(openChats.filter(chat => chat.id !== memberId));
  };

  const handleSendChatMessage = async (memberId, message) => {
    const chat = openChats.find(c => c.id === memberId);
    if (!chat) return;
    
    setOpenChats(openChats.map(c => 
      c.id === memberId 
        ? { ...c, messages: [...c.messages, { text: message, sender: 'me', timestamp: new Date() }] }
        : c
    ));
  };

  const handleNewMessage = (chatId, message) => {
    setOpenChats(openChats.map(chat => 
      chat.id === chatId 
        ? { ...chat, messages: [...chat.messages, message] }
        : chat
    ));
  };


  const handleEditMessage = (messageId, content) => {
    setEditingMessageId(messageId);
    setEditingContent(content);
  };

  const handleSaveEdit = async (messageIndex) => {
    if (!editingContent.trim()) return;

    const updatedMessages = [...conversation.messages];
    updatedMessages[messageIndex] = {
      ...updatedMessages[messageIndex],
      content: editingContent,
      edited: true
    };

    const messagesUpToEdit = updatedMessages.slice(0, messageIndex);

    await onUpdateConversation(conversation.id, {
      messages: messagesUpToEdit
    });

    // Pass the truncated messages directly to handleSendMessage to avoid using stale state
    handleSendMessage(editingContent, null, null, messagesUpToEdit);
  };

  const handleCopyMessage = async (content, messageId) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handlePlayVoiceMessage = async (message) => {
    try {
      // Use the text-to-speech API to play the transcription
      const response = await fetch(`${AUTH_BASE_URL}/api/audio/synthesize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          text: message.transcription || message.content,
          voice: selectedVoice || 'alloy',
          format: 'mp3'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to synthesize voice');
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
      };
      
      await audio.play();
    } catch (error) {
      console.error('Failed to play voice message:', error);
    }
  };

  const handleResendMessage = async (messageIndex, newModel = null) => {
    const userMessage = conversation?.messages?.[messageIndex - 1];
    if (userMessage && userMessage.role === 'user') {
      // Keep only messages up to and including the user message that triggered this response
      // This removes the assistant message being regenerated AND all messages after it
      const messagesUpToUser = (conversation?.messages || []).slice(0, messageIndex);

      // Update the conversation with truncated messages
      await onUpdateConversation(conversation.id, {
        messages: messagesUpToUser
      });

      // Pass the truncated messages directly to handleSendMessage to avoid using stale state
      handleSendMessage(userMessage.content, newModel, null, messagesUpToUser);
    }
    setResendDropdownId(null);
  };

  // Show Context Spaces interface when requested (before any other conditions)
  if (showContextSpaces) {
    return (
      <ContextSpacesPage 
        onBack={() => setShowContextSpaces(false)}
        onSelectContextSpace={(contextSpace) => {
          setShowContextSpaces(false);
          handleSelectContextSpace(contextSpace);
        }}
      />
    );
  }

  // Show centered welcome screen when no conversation is selected or for new conversations (but not for app conversations)
  if (!conversation || ((!conversation.messages || conversation.messages.length === 0) && !isStreaming && !isAppConversation)) {
    return (
      <div 
        className="flex-1 flex h-full relative"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Hidden File Input for Welcome Screen */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,application/pdf,.doc,.docx,.txt,.md,.csv,.xlsx"
          onChange={handleDirectFileSelection}
          style={{ display: 'none' }}
        />
        
        {isDragging && (
          <div className={`absolute inset-0 z-50 border-2 border-dashed flex items-center justify-center ${
            currentModelSupportsVision() 
              ? 'bg-blue-500 bg-opacity-20 border-blue-500' 
              : 'bg-red-500 bg-opacity-20 border-red-500'
          }`}>
            <div className="bg-white rounded-lg p-6 shadow-lg text-center max-w-md">
              {currentModelSupportsVision() ? (
                <>
                  <div className="text-blue-600 text-2xl mb-2">📁</div>
                  <div className="text-lg font-medium text-gray-800">Drop files here to upload</div>
                  <div className="text-sm text-gray-600">Images and documents supported</div>
                </>
              ) : (
                <>
                  <div className="text-red-600 text-2xl mb-2">🚫</div>
                  <div className="text-lg font-medium text-gray-800">File upload not supported</div>
                  <div className="text-sm text-gray-600 mb-3">
                    The current model "{selectedModel}" doesn't support file processing
                  </div>
                  <div className="text-sm text-blue-600 font-medium">
                    Switch to a vision model (GPT-4o, Claude, or Gemini) to upload files
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        <div className={`flex-1 flex flex-col appearance-${appearanceMode} ${
          isSuppleMode ? 'supple-card-frosted' : ''
        }`}>
          <header className={`px-6 py-2 flex items-center justify-between ${
            isSuppleMode 
              ? 'supple-card-glass supple-border-subtle backdrop-blur-md' 
              : 'border-claude-border bg-white'
          }`}>
          <div className="flex items-center flex-1 min-w-0">
            {/* Mobile hamburger button */}
            <button
              onClick={onToggleSidebar}
              className="p-2 mr-2 md:hidden hover:bg-gray-100 rounded-lg transition-colors"
              title="Toggle sidebar"
            >
              <Menu className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-sm md:text-lg font-semibold text-claude-text truncate flex-1 min-w-0">
              {conversation?.title || 'New Chat'}
            </h1>

            {/* Seasonal Theme Header */}
            <div className="ml-2 flex-shrink-0">
              <SeasonalHeaderDecoration />
            </div>

            {/* Context Space Indicator */}
            {selectedContextSpaceId && (
              <div className="ml-2 relative flex-shrink-0" data-space-dropdown>
                <button
                  onClick={() => setShowSpaceDropdown(!showSpaceDropdown)}
                  className="flex items-center gap-1 px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-full text-xs font-medium border border-blue-200 transition-colors relative"
                  data-tour="context-spaces"
                >
                  <FolderOpen className="w-3 h-3" />
                  <span>
                    {contextSpaceContext?.name || conversation?.contextSpaces?.[0]?.contextSpace?.name || 'Context Space'}
                  </span>
                  {(contextSpaceContext?.icon || conversation?.contextSpaces?.[0]?.contextSpace?.icon) && (
                    <span className="text-sm">
                      {contextSpaceContext?.icon || conversation?.contextSpaces?.[0]?.contextSpace?.icon}
                    </span>
                  )}
                  <ChevronDown className="w-3 h-3" />
                  <div className="absolute -top-1 -right-1">
                    <NewFeatureTag featureId="context-spaces" size="xs" />
                  </div>
                </button>
                
                {/* Space Dropdown */}
                {showSpaceDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    <div className="p-2">
                      <button
                        onClick={() => {
                          setShowContextSpaces && setShowContextSpaces(true);
                          setShowSpaceDropdown(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-2"
                      >
                        <FolderOpen className="w-4 h-4" />
                        Change Space
                      </button>
                      <button
                        onClick={() => {
                          onExitContextSpace && onExitContextSpace();
                          setShowSpaceDropdown(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-2"
                      >
                        <X className="w-4 h-4" />
                        Exit Space
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={handleShareConversation}
              className="ml-2 p-1.5 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              title="Share conversation"
            >
              <Share2 className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={() => onOpenMemory && onOpenMemory()}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              title="Memory - Set your AI preferences"
            >
              <Brain className="w-4 h-4 text-gray-600" />
            </button>
          </div>
          <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
            {/* New Chat button - mobile only */}
            <button
              onClick={onNewConversation}
              className="md:hidden p-2 bg-orange-500 hover:bg-orange-600 text-white rounded-full transition-colors"
              title="New chat"
            >
              <Plus className="w-4 h-4" />
            </button>
            
            {(userCredits || true) && (
              <div className={`flex items-center gap-2 text-sm ${
                userCredits ? (
                  userCredits && userCredits.creditsRemaining <= 0 ? 'text-red-600' :
                  userCredits.monthlyCredits && userCredits.creditsRemaining <= userCredits.monthlyCredits * 0.1 ? 'text-yellow-600' :
                  'text-gray-600'
                ) : 'text-gray-500'
              }`}>
                {isLargeScreen ? (
                  <button
                    onClick={() => setShowCreditsModal(true)}
                    className="flex items-center gap-2 px-2 py-1 hover:bg-gray-100 rounded-lg transition-colors"
                    title={userCredits && userCredits.monthlyCredits ? `View credits - ${Math.round(((userCredits.monthlyCredits - userCredits.creditsRemaining) / userCredits.monthlyCredits) * 100)}% used this month` : 'View credits - Loading...'}
                  >
                    <div className="relative">
                      {/* Circular progress border */}
                      <div className="relative w-6 h-6 flex items-center justify-center">
                        {userCredits && userCredits.monthlyCredits && (
                          <svg className="absolute inset-0 w-6 h-6 transform -rotate-90">
                            <circle
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="2"
                              fill="transparent"
                              className="text-gray-200"
                            />
                            <circle
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="2"
                              fill="transparent"
                              strokeDasharray={`${2 * Math.PI * 10}`}
                              strokeDashoffset={`${2 * Math.PI * 10 * (1 - Math.min(((userCredits.monthlyCredits - userCredits.creditsRemaining) / userCredits.monthlyCredits), 1))}`}
                              className={`transition-all duration-300 ${
                                userCredits.creditsRemaining <= 0 ? 'text-red-500' :
                                ((userCredits.monthlyCredits - userCredits.creditsRemaining) / userCredits.monthlyCredits) >= 0.7 ? 'text-orange-500' :
                                'text-green-500'
                              }`}
                              strokeLinecap="round"
                            />
                          </svg>
                        )}
                        {(!userCredits || !userCredits.monthlyCredits) && (
                          <svg className="absolute inset-0 w-6 h-6 transform -rotate-90">
                            <circle
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="2"
                              fill="transparent"
                              className="text-gray-300"
                            />
                          </svg>
                        )}
                        <CreditCard className="w-3 h-3 relative z-10" />
                      </div>
                    </div>
                    <span>{userCredits && userCredits.monthlyCredits ? `${userCredits.creditsRemaining.toLocaleString()} / ${userCredits.monthlyCredits.toLocaleString()}` : 'Loading...'}</span>
                  </button>
                ) : (
                  <button
                    onClick={() => setShowCreditsModal(true)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors relative"
                    title={userCredits && userCredits.monthlyCredits ? `View credits - ${Math.round(((userCredits.monthlyCredits - userCredits.creditsRemaining) / userCredits.monthlyCredits) * 100)}% used this month` : 'View credits - Loading...'}
                  >
                    {/* Circular progress border */}
                    <div className="relative w-6 h-6 flex items-center justify-center">
                      {userCredits && (
                        <svg className="absolute inset-0 w-6 h-6 transform -rotate-90">
                          <circle
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="2"
                            fill="transparent"
                            className="text-gray-200"
                          />
                          <circle
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="2"
                            fill="transparent"
                            strokeDasharray={`${2 * Math.PI * 10}`}
                            strokeDashoffset={`${2 * Math.PI * 10 * (1 - Math.min(((userCredits.monthlyCredits - userCredits.creditsRemaining) / userCredits.monthlyCredits), 1))}`}
                            className={`transition-all duration-300 ${
                              userCredits.creditsRemaining <= 0 ? 'text-red-500' :
                              ((userCredits.monthlyCredits - userCredits.creditsRemaining) / userCredits.monthlyCredits) >= 0.7 ? 'text-orange-500' :
                              'text-green-500'
                            }`}
                            strokeLinecap="round"
                          />
                        </svg>
                      )}
                      <CreditCard className="w-3 h-3 relative z-10" />
                    </div>
                  </button>
                )}
              </div>
            )}
          </div>
        </header>

        {/* Context Health Indicator */}
        {conversation?.id && !conversation.id.startsWith('temp-') && (
          <div className="px-6 py-2 border-b border-gray-200">
            <ContextHealthIndicator
              conversationId={conversation.id}
              onCompactionComplete={(result) => {
                // Reload conversation to show compacted messages
                console.log('Context optimized:', result);
                // You could add a toast notification here
              }}
            />
          </div>
        )}

        <div className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-4xl w-full">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-semibold text-claude-text mb-2">Welcome to Supple Agentic Chat</h2>
              <p className="text-claude-muted">Start a conversation with GPT, Claude, Gemini, and Grok models</p>
            </div>
            
            {/* AI Apps Widget - Admin only */}
            {showAIAppsWidget && isAdmin && (
              <div className="mb-6">
                <AIAppsWidget 
                  onSelectApp={(app) => {
                    // Just select the app, don't show any expanded view
                    setSelectedApp(app);
                    setConversationApp({
                      appId: app.id,
                      appName: app.name,
                      systemPrompt: app.systemPrompt,
                      ...app.metadata
                    });
                    setAppOptions(app.metadata?.options || {});
                    setShowAIAppsWidget(false);
                  }}
                  onOpenFullView={() => {
                    setShowAIApps(true);
                  }}
                />
              </div>
            )}

            {/* Context Spaces Widget - Show for all users */}
            {/* {showContextSpacesWidget && (
              <div className="mb-6">
                <ContextSpacesWidget 
                  onSelectContextSpace={handleSelectContextSpace}
                  onOpenFullView={handleOpenContextSpacesFullView}
                />
              </div>
            )} */}
            
            {/* Context Space Information Message */}
            {selectedContextSpaceId && (
              <div className="max-w-4xl mx-auto mb-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FolderOpen className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="text-sm font-medium text-blue-900">
                          You are in the "{contextSpaceContext?.name || conversation?.contextSpaces?.[0]?.contextSpace?.name || 'Context Space'}" context space
                        </p>
                        <p className="text-xs text-blue-700 mt-1">
                          Sending a message will start the conversation in this context space with access to its resources.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowContextSpaces && setShowContextSpaces(true)}
                        className="text-xs px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-md transition-colors"
                      >
                        Change Space
                      </button>
                      <button
                        onClick={() => onExitContextSpace && onExitContextSpace()}
                        className="text-xs px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
                      >
                        Exit Space
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Unified Welcome Input Area */}
            <div className="max-w-4xl mx-auto">
              {/* Main Input Container - Matching conversation interface */}
              <div className="border border-gray-300 rounded-2xl p-4 bg-white shadow-sm mb-3">
                {/* AI App Selector */}
                {selectedApp && (
                  <div className="mb-3 pb-3 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`${selectedApp.color || 'bg-purple-500'} p-2 rounded-lg text-white`}>
                          {selectedApp.icon && <selectedApp.icon className="w-4 h-4" />}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{selectedApp.name}</div>
                          <div className="text-xs text-gray-500">{selectedApp.description}</div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedApp(null);
                          setAppOptions({});
                          setConversationApp(null);
                        }}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Remove
                      </button>
                    </div>
                    
                    {/* App Options Buttons */}
                    {selectedApp.id === 'draft-composer' && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowAppOptionModal({ type: 'emailThread', show: true });
                          }}
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs transition-colors ${
                            appOptions.emailThread 
                              ? 'bg-blue-50 text-blue-700 hover:bg-blue-100' 
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <Mail className="w-3 h-3" />
                          {appOptions.emailThread ? 'Thread added' : 'Add thread'}
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowAppOptionModal({ type: 'tone', show: true });
                          }}
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs transition-colors ${
                            appOptions.tone
                              ? 'bg-purple-50 text-purple-700 hover:bg-purple-100' 
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <Palette className="w-3 h-3" />
                          {appOptions.tone ? appOptions.tone.charAt(0).toUpperCase() + appOptions.tone.slice(1) : 'Set tone'}
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowAppOptionModal({ type: 'purpose', show: true });
                          }}
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs transition-colors ${
                            appOptions.purpose
                              ? 'bg-green-50 text-green-700 hover:bg-green-100' 
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <Target className="w-3 h-3" />
                          {appOptions.purpose 
                            ? appOptions.purpose.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) 
                            : 'Set purpose'}
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowAppOptionModal({ type: 'multipleVersions', show: true });
                          }}
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs transition-colors ${
                            appOptions.multipleVersions
                              ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' 
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <Sparkles className="w-3 h-3" />
                          {appOptions.multipleVersions ? 'Multiple versions' : 'Versions'}
                        </button>
                        
                        {/* System Prompt Indicator */}
                        <div className="inline-flex items-center gap-1 px-3 py-1 bg-gray-50 text-gray-600 rounded-md text-xs">
                          <Settings className="w-3 h-3" />
                          System prompt active
                        </div>
                      </div>
                    )}
                    
                    {selectedApp.id === 'competitor-analyzer' && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowAppOptionModal({ type: 'yourProduct', show: true });
                          }}
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs transition-colors ${
                            appOptions.yourProduct 
                              ? 'bg-blue-50 text-blue-700 hover:bg-blue-100' 
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <Globe className="w-3 h-3" />
                          {appOptions.yourProduct ? 'Your URL set' : 'Add your URL'}
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowAppOptionModal({ type: 'competitors', show: true });
                          }}
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs transition-colors ${
                            appOptions.competitors
                              ? 'bg-red-50 text-red-700 hover:bg-red-100' 
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <TrendingUp className="w-3 h-3" />
                          {appOptions.competitors ? `${appOptions.competitors.split(',').length} competitors` : 'Add competitors'}
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowAppOptionModal({ type: 'keywords', show: true });
                          }}
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs transition-colors ${
                            appOptions.keywords
                              ? 'bg-purple-50 text-purple-700 hover:bg-purple-100' 
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <Search className="w-3 h-3" />
                          {appOptions.keywords ? 'Keywords set' : 'Set keywords'}
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowAppOptionModal({ type: 'analysisType', show: true });
                          }}
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs transition-colors ${
                            appOptions.analysisType
                              ? 'bg-green-50 text-green-700 hover:bg-green-100' 
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <BarChart className="w-3 h-3" />
                          {appOptions.analysisType 
                            ? appOptions.analysisType.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) 
                            : 'Analysis type'}
                        </button>
                        
                        {/* System Prompt Indicator */}
                        <div className="inline-flex items-center gap-1 px-3 py-1 bg-gray-50 text-gray-600 rounded-md text-xs">
                          <Settings className="w-3 h-3" />
                          System prompt active
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Debug - Remove this later */}
                {false && (
                  <button 
                    onClick={() => setShowAppOptionModal('emailThread')}
                    className="mb-2 px-4 py-2 bg-red-500 text-white rounded"
                  >
                    Test Modal (showAppOptionModal: {showAppOptionModal || 'null'})
                  </button>
                )}
                
                {/* Credit Warning Banner */}
                {(userCredits || true) && (
                  (() => {
                    const usagePercentage = userCredits && userCredits.monthlyCredits ? ((userCredits.monthlyCredits - userCredits.creditsRemaining) / userCredits.monthlyCredits) * 100 : 0;
                    const isOutOfCredits = userCredits && userCredits.creditsRemaining <= 0;
                    const isApproachingLimit = usagePercentage >= 80 && !isOutOfCredits;
                    
                    if (isOutOfCredits) {
                      return (
                        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4 text-red-600" />
                              <span className="text-sm font-medium text-red-800">
                                Credit limit reached. Contact administrator.
                              </span>
                            </div>
                            {!currentUser?.hasUsedBoost && (
                              <button
                                onClick={async () => {
                                  try {
                                    await handleUpdateCredits({
                                      action: 'topup',
                                      amount: 10000,
                                      userId: currentUser?.id
                                    });
                                  } catch (error) {
                                    console.error('Failed to add grace credits:', error);
                                  }
                                }}
                                className="px-3 py-1 bg-purple-600 text-white text-xs rounded-md hover:bg-purple-700 transition-colors"
                              >
                                Borrow 10K Grace Credits
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    } else if (isApproachingLimit) {
                      return (
                        <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-yellow-600" />
                            <span className="text-sm text-yellow-800">
                              You are approaching your credit limit ({Math.round(usagePercentage)}% used)
                            </span>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()
                )}

                {/* Message Input Area */}
                <div className="relative">
                  <textarea
                    ref={welcomeTextareaRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder="Ask me anything..."
                    disabled={isLoading}
                    className="w-full resize-none border-none outline-none text-lg disabled:opacity-50 disabled:cursor-not-allowed bg-transparent pr-32"
                    rows={4}
                    style={{ minHeight: '140px', maxHeight: '300px' }}
                  />
                  
                  {/* Top Right: Send and Voice Chat Buttons */}
                  <div className="absolute top-3 right-3 flex items-center gap-2">
                    {/* Send Button */}
                    <button
                      data-send-button
                      onClick={() => handleSendMessage()}
                      disabled={!message.trim() || isLoading}
                      className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white p-3 rounded-xl transition-colors shadow-sm"
                    >
                      <Send className="w-5 h-5" />
                    </button>

                    {/* Voice Chat Mode */}
                    <button
                      onClick={() => {
                        console.log('Voice chat button clicked');
                        console.log('Current conversation:', conversation);
                        console.log('Has messages:', conversation?.messages?.length);
                        
                        // If there's no conversation, create one first so audio view is in conversation view
                        if (!conversation || !conversation.messages || conversation.messages.length === 0) {
                          console.log('Creating new conversation for audio chat');
                          onNewConversation?.();
                        }
                        
                        // Open the voice chat
                        setShowAudioConversation(true);
                      }}
                      className="p-3 bg-green-500 hover:bg-green-600 text-white rounded-xl transition-colors shadow-sm relative"
                      title="Voice Chat"
                      data-tour="voice-chat"
                    >
                      <Mic className="w-5 h-5" />
                      <div className="absolute -top-1 -right-1">
                        <BetaTag 
                          title="Voice Chat" 
                          description="Have natural conversations using voice input and output. This feature is in beta." 
                          size="xs" 
                        />
                      </div>
                    </button>
                  </div>
                </div>

                {/* Bottom Row: + Button, Model, Dictate Button */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  {/* Left: + Button with comprehensive dropdown */}
                  <div className="relative" ref={welcomeOptionsRef}>
                    <button
                      onClick={() => setShowWelcomeOptionsDropdown(!showWelcomeOptionsDropdown)}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-medium"
                    >
                      <Plus className="w-4 h-4" />
                      Options
                    </button>
                    
                    {/* Comprehensive Options Dropdown */}
                    {showWelcomeOptionsDropdown && (
                      <div className="absolute bottom-full left-0 mb-2 w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-1">
                        <div className="space-y-1">
                          {/* Mode Section */}
                          <div className="p-2">
                            <div className="text-xs font-medium text-gray-500 mb-2">Mode</div>
                            <div className="flex gap-1">
                              <button
                                onClick={() => {
                                  setEnableAutoTools(true);
                                }}
                                className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                                  enableAutoTools
                                    ? 'bg-purple-100 text-purple-700 border border-purple-200'
                                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                                }`}
                              >
                                <Zap className="w-4 h-4" />
                                Auto
                              </button>
                              <button
                                onClick={() => {
                                  setEnableAutoTools(false);
                                  // Close other dropdowns first
                                  setShowAIAppsDropdown(false);
                                  setShowSlashCommandsDropdown(false);
                                  setShowVoiceDropdown(false);
                                  // Then open Manual Tools dropdown
                                  setShowManualToolsDropdown(true);
                                }}
                                className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                                  !enableAutoTools
                                    ? 'bg-blue-100 text-blue-700 border border-blue-200'
                                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                                }`}
                              >
                                <Settings className="w-4 h-4" />
                                Manual
                                {!enableAutoTools && enabledTools.length > 0 && (
                                  <span className="ml-1 bg-blue-200 text-blue-700 text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                    {enabledTools.length}
                                  </span>
                                )}
                              </button>
                            </div>
                          </div>

                          <div className="border-t border-gray-100"></div>

                          {/* AI Apps */}
                          <div className="relative">
                            <button
                              onClick={() => {
                                // Close other dropdowns first
                                setShowVoiceDropdown(false);
                                setShowSlashCommandsDropdown(false);
                                setShowManualToolsDropdown(false);
                                // Then open AI Apps dropdown
                                setShowAIAppsDropdown(true);
                              }}
                              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg text-sm"
                            >
                              <Sparkles className="w-4 h-4 text-purple-600" />
                              <span>AI Apps</span>
                              {selectedApp && (
                                <span className="ml-auto text-xs text-gray-500">({selectedApp.name})</span>
                              )}
                              <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
                            </button>
                            
                            {/* AI Apps Dropdown - Opens to the right of this button */}
                            {showAIAppsDropdown && (
                              <div className="absolute top-0 left-full ml-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-50">
                                <div className="text-xs font-medium text-gray-500 mb-2 px-2">Select AI Assistant</div>
                                <div className="max-h-80 overflow-y-auto space-y-1">
                                  {availableApps.map((app) => (
                                    <button
                                      key={app.id}
                                      onClick={() => {
                                        setSelectedApp(app);
                                        setConversationApp({
                                          appId: app.id,
                                          appName: app.name,
                                          systemPrompt: app.systemPrompt,
                                          icon: undefined,
                                          color: app.color,
                                          description: app.description
                                        });
                                        setShowAIAppsDropdown(false);
                                        setShowWelcomeOptionsDropdown(false);
                                        setAppOptions({});
                                      }}
                                      className={`w-full flex items-center gap-2 p-2 rounded-lg text-left transition-colors ${
                                        selectedApp?.id === app.id 
                                          ? 'bg-purple-50 text-purple-700 border border-purple-200' 
                                          : 'hover:bg-gray-50 text-gray-700'
                                      }`}
                                    >
                                      <div className={`${app.color} p-1.5 rounded text-white flex-shrink-0`}>
                                        <app.icon className="w-3 h-3" />
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium truncate">{app.name}</div>
                                        <div className="text-xs opacity-75 truncate">{app.description}</div>
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Slash Commands */}
                          <div className="relative">
                            <button
                              onClick={() => {
                                // Close other dropdowns first
                                setShowAIAppsDropdown(false);
                                setShowVoiceDropdown(false);
                                setShowManualToolsDropdown(false);
                                // Then open Slash Commands dropdown
                                setShowSlashCommandsDropdown(true);
                              }}
                              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg text-sm"
                            >
                              <Slash className="w-4 h-4 text-blue-600" />
                              <span>Slash Commands</span>
                              <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
                            </button>
                            
                            {/* Slash Commands Dropdown - Opens to the right of this button */}
                            {showSlashCommandsDropdown && (
                              <div className="absolute top-0 left-full ml-2 bg-white border border-gray-200 rounded-lg shadow-lg p-2 w-64 z-50">
                                <div className="text-xs font-medium text-gray-500 mb-2 px-2">Slash Commands</div>
                                <div className="space-y-1">
                                  {[
                                    { command: '/rank', desc: 'Check SEO ranking positions', icon: '📈' },
                                    { command: '/ads', desc: 'Analyze advertisement data', icon: '🎯' },
                                    { command: '/analytics', desc: 'View website analytics', icon: '📊' },
                                    { command: '/calls', desc: 'Call tracking data', icon: '📞' },
                                    { command: '/audit', desc: 'SEO audit report', icon: '🔍' }
                                  ].map((item) => (
                                    <button
                                      key={item.command}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setMessage(prev => prev + item.command + ' ');
                                        setShowSlashCommandsDropdown(false);
                                        setShowWelcomeOptionsDropdown(false);
                                        // Focus back to textarea
                                        setTimeout(() => {
                                          welcomeTextareaRef.current?.focus();
                                        }, 100);
                                      }}
                                      className="w-full text-left px-3 py-2 text-sm rounded transition-colors hover:bg-gray-50 flex items-center gap-2"
                                    >
                                      <span className="text-base">{item.icon}</span>
                                      <div>
                                        <div className="font-medium text-gray-900">{item.command}</div>
                                        <div className="text-xs text-gray-500">{item.desc}</div>
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Add Files & Photo */}
                          <button
                            onClick={() => {
                              setShowWelcomeOptionsDropdown(false);
                              triggerFileInput();
                            }}
                            disabled={!currentModelSupportsVision()}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${
                              currentModelSupportsVision() 
                                ? 'hover:bg-gray-50 text-gray-700' 
                                : 'text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            <Paperclip className="w-4 h-4 text-green-600" />
                            <span>Add Files & Photo</span>
                            {attachedFiles.length > 0 && (
                              <span className="ml-auto bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                {attachedFiles.length}
                              </span>
                            )}
                          </button>

                          {/* AI Voices */}
                          <div className="relative">
                            <button
                              onClick={() => {
                                // Close other dropdowns first
                                setShowAIAppsDropdown(false);
                                setShowSlashCommandsDropdown(false);
                                setShowManualToolsDropdown(false);
                                // Then open Voice dropdown
                                setShowVoiceDropdown(true);
                              }}
                              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg text-sm"
                            >
                              <Mic className="w-4 h-4 text-indigo-600" />
                              <span>AI Voices</span>
                              <span className="ml-auto text-xs text-gray-500">
                                {selectedVoice.charAt(0).toUpperCase() + selectedVoice.slice(1)}
                              </span>
                              <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
                            </button>
                            
                            {/* Voice Selection Dropdown - Opens to the right of this button */}
                            {showVoiceDropdown && (
                              <div ref={voiceDropdownRef} className="voice-dropdown absolute top-0 left-full ml-2 bg-white border border-gray-200 rounded-lg shadow-lg p-2 w-48 z-50" data-dropdown="voice">
                                <div className="text-xs font-medium text-gray-500 mb-2 px-2">Select Voice</div>
                                <div className="space-y-1">
                                  {['alloy', 'echo', 'shimmer', 'sage', 'ballad', 'coral', 'ash', 'verse'].map((voice) => (
                                    <button
                                      key={voice}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedVoice(voice);
                                        setShowVoiceDropdown(false);
                                      }}
                                      className={`w-full text-left px-3 py-2 text-sm rounded transition-colors ${
                                        selectedVoice === voice
                                          ? 'bg-blue-50 text-blue-700'
                                          : 'text-gray-700 hover:bg-gray-50'
                                      }`}
                                    >
                                      {voice.charAt(0).toUpperCase() + voice.slice(1)}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Reasoning Effort - Only for supported models */}
                          {selectedModel?.includes('gpt-5') && (
                            <div className="px-3 py-2">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm text-gray-700 flex items-center gap-2">
                                  <Brain className="w-4 h-4 text-purple-600" />
                                  Reasoning Effort
                                </span>
                              </div>
                              <select
                                value={gpt5ReasoningEffort}
                                onChange={(e) => setGpt5ReasoningEffort(e.target.value)}
                                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                              >
                                <option value="minimal">Minimal</option>
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                              </select>
                            </div>
                          )}

                          {/* Verbosity - Only for supported models */}
                          {selectedModel?.includes('gpt-5') && (
                            <div className="px-3 py-2">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm text-gray-700 flex items-center gap-2">
                                  <MessageSquare className="w-4 h-4 text-orange-600" />
                                  Verbosity
                                </span>
                              </div>
                              <select
                                value={gpt5Verbosity}
                                onChange={(e) => setGpt5Verbosity(e.target.value)}
                                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                              >
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                              </select>
                            </div>
                          )}

                          {/* 1M Context Toggle - Only for Claude Sonnet 4.5 */}
                          {supports1MContext(selectedModel) && (
                            <div className="px-3 py-2 border-t border-gray-200">
                              <label className="flex items-center justify-between cursor-pointer group">
                                <div className="flex items-center gap-2">
                                  <Database className="w-4 h-4 text-blue-600" />
                                  <span className="text-sm text-gray-700 group-hover:text-gray-900">
                                    1M Context Window
                                  </span>
                                  <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">BETA</span>
                                </div>
                                <input
                                  type="checkbox"
                                  checked={use1MContext}
                                  onChange={(e) => setUse1MContext(e.target.checked)}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                />
                              </label>
                              <p className="text-xs text-gray-500 mt-1 ml-6">
                                Enables 1M token context (vs standard 200K). Additional charges apply.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Manual Tools Dropdown - Opens to the right */}
                    {!enableAutoTools && showManualToolsDropdown && (
                      <div className="absolute bottom-0 left-full ml-2 w-72 p-3 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                        <div className="text-xs font-medium text-gray-500 mb-2">Select Tools</div>
                        <div className="grid grid-cols-2 gap-1">
                          {availableTools.map(tool => {
                            const Icon = tool.icon;
                            const isEnabled = enabledTools.includes(tool.id);
                            return (
                              <button
                                key={tool.id}
                                onClick={() => toggleTool(tool.id)}
                                className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-xs transition-all ${
                                  isEnabled 
                                    ? `${tool.bgColor} ${tool.textColor} border border-opacity-50`
                                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                                }`}
                                title={tool.description}
                              >
                                <Icon className="w-3 h-3" />
                                <span className="font-medium">{tool.name}</span>
                                {isEnabled && (
                                  <CheckCircle className="w-2 h-2 ml-auto" />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                  </div>

                  {/* Right: Model Selection, MCP Selector, and Dictate Button */}
                  <div className="flex items-center gap-3">
                    {/* Model Selection */}
                    <div className="min-w-[200px] relative" data-tour="model-dropdown">
                      <ModelSelector
                        models={models}
                        selectedModel={selectedModel}
                        onModelChange={setSelectedModel}
                        burstGroups={modelGroups}
                        selectedBurstGroup={selectedBurstGroup}
                        onBurstGroupChange={setSelectedBurstGroup}
                        burstMode={burstMode}
                        onModeChange={setBurstMode}
                        topModelsConfig={topModelsConfig}
                      />
                    </div>

                    {/* MCP Selector */}
                    <MCPSelector button={true} />

                    {/* Dictate Mode (Speech-to-Text) */}
                    <button
                      onClick={() => {
                        // Add dictate functionality here
                        console.log('Dictate mode clicked');
                      }}
                      className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex-shrink-0"
                      title="Dictate (Speech to Text)"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>



              <p className="text-xs text-gray-500 text-center">
                Press Enter to send, Shift+Enter for new line
              </p>

              {/* Attached Files Display for Welcome Screen */}
              {attachedFiles.length > 0 && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    Attached Files ({attachedFiles.length})
                  </h4>
                  <div className="space-y-2">
                    {attachedFiles.map((file) => (
                      <div
                        key={file.fileId}
                        className="flex items-center gap-3 p-2 bg-white rounded border"
                      >
                        <div className="flex-shrink-0">
                          {file.isImage ? (
                            <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                              <ImageIcon className="w-4 h-4 text-blue-600" />
                            </div>
                          ) : (
                            <div className="w-8 h-8 bg-gray-200 rounded flex items-center justify-center">
                              <FileText className="w-4 h-4 text-gray-600" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {file.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {file.type} • {(file.size / 1024).toFixed(1)}KB
                          </div>
                        </div>
                        {file.isImage && file.url && (
                          <button
                            onClick={() => setModalImage(file.url)}
                            className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            Preview
                          </button>
                        )}
                        <button
                          onClick={() => removeAttachedFile(file.fileId)}
                          className="p-1 rounded hover:bg-red-100 text-red-500"
                          title="Remove file"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* AI Assistant and Context Spaces Feature Blocks */}
            {(showWelcomeAIBlock || showWelcomeContextBlock) && (
              <div className="max-w-4xl mx-auto mt-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* AI Assistant Block */}
                  {showWelcomeAIBlock && (
                    <div className={`relative p-6 rounded-xl border transition-all hover:shadow-lg ${
                      isSuppleMode 
                        ? 'supple-card-glass supple-border-subtle backdrop-blur-md' 
                        : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 hover:border-blue-300'
                    }`}>
                      {/* Close Button */}
                      <button
                        onClick={() => setShowWelcomeAIBlock(false)}
                        className={`absolute top-3 right-3 p-1 rounded-full transition-colors ${
                          isSuppleMode 
                            ? 'supple-text-muted hover:supple-text-primary hover:supple-card-glass-hover' 
                            : 'text-gray-400 hover:text-gray-600 hover:bg-white/50'
                        }`}
                        title="Hide AI Assistant block"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <div className="flex items-center gap-3 mb-4">
                    <div className={`p-3 rounded-lg ${
                      isSuppleMode ? 'supple-btn-gradient-blue' : 'bg-blue-500'
                    }`}>
                      <Bot className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold ${
                        isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
                      }`}>
                        AI Assistant
                      </h3>
                      <p className={`text-sm ${
                        isSuppleMode ? 'supple-text-muted' : 'text-gray-600'
                      }`}>
                        Chat with multiple AI models
                      </p>
                    </div>
                  </div>
                  <p className={`text-sm mb-4 leading-relaxed ${
                    isSuppleMode ? 'supple-text-muted' : 'text-gray-700'
                  }`}>
                    Chat with GPT-4, Claude, Gemini, and Grok. Switch models mid-conversation, compare responses, and find the best AI for your needs.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      isSuppleMode ? 'supple-card-glass' : 'bg-blue-100 text-blue-700'
                    }`}>
                      Multi-Model
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      isSuppleMode ? 'supple-card-glass' : 'bg-indigo-100 text-indigo-700'
                    }`}>
                      Real-time
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      isSuppleMode ? 'supple-card-glass' : 'bg-purple-100 text-purple-700'
                    }`}>
                      Smart
                    </span>
                  </div>
                    </div>
                  )}

                  {/* MCP Integration Block */}
                  {showWelcomeContextBlock && (
                    <div className={`relative p-6 rounded-xl border transition-all hover:shadow-lg ${
                      isSuppleMode
                        ? 'supple-card-glass supple-border-subtle backdrop-blur-md hover:supple-card-glass-hover'
                        : 'bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200 hover:border-purple-300 hover:bg-gradient-to-br hover:from-purple-100 hover:to-indigo-100'
                    }`}>
                      {/* Close Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowWelcomeContextBlock(false);
                        }}
                        className={`absolute top-3 right-3 p-1 rounded-full transition-colors ${
                          isSuppleMode 
                            ? 'supple-text-muted hover:supple-text-primary hover:supple-card-glass-hover' 
                            : 'text-gray-400 hover:text-gray-600 hover:bg-white/50'
                        }`}
                        title="Hide MCP block"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      {/* Clickable Area for Context Spaces */}
                      <div 
                        onClick={() => setShowContextSpaces && setShowContextSpaces(true)}
                        className="cursor-pointer"
                      >
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`p-3 rounded-lg ${
                      isSuppleMode ? 'supple-btn-gradient-purple' : 'bg-gradient-to-br from-purple-500 to-indigo-500'
                    }`}>
                      <Code className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold ${
                        isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
                      }`}>
                        MCP Integration
                      </h3>
                      <p className={`text-sm ${
                        isSuppleMode ? 'supple-text-muted' : 'text-gray-600'
                      }`}>
                        Connect external tools and data
                      </p>
                    </div>
                  </div>
                  <p className={`text-sm mb-4 leading-relaxed ${
                    isSuppleMode ? 'supple-text-muted' : 'text-gray-700'
                  }`}>
                    Model Context Protocol (MCP) lets you connect AI to external tools like Google Ads, databases, file systems, and more. Access real-time data and automate workflows directly from your conversations.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      isSuppleMode ? 'supple-card-glass' : 'bg-purple-100 text-purple-700'
                    }`}>
                      External Tools
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      isSuppleMode ? 'supple-card-glass' : 'bg-indigo-100 text-indigo-700'
                    }`}>
                      Data Integration
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      isSuppleMode ? 'supple-card-glass' : 'bg-blue-100 text-blue-700'
                    }`}>
                      Automation
                    </span>
                  </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Real-time Audio Conversation Overlay for Welcome Screen */}
        {showAudioConversation && (
          <RealtimeAudioOverlay
            onClose={() => setShowAudioConversation(false)}
            selectedVoice={selectedVoice}
            onVoiceChange={setSelectedVoice}
            existingConversation={conversation?.messages || []}
            selectedModel={selectedModel || 'gpt-4o-audio-preview'}
            currentUser={currentUser}
            isFromWelcome={true}
            onUpdateConversation={(voiceMessage) => {
              console.log('🚀 Voice message callback called, conversation exists:', !!conversation);
              console.log('🚀 Voice message:', voiceMessage);
              if (onUpdateConversation) {
                // Pass a special signal to parent to handle conversation creation
                onUpdateConversation(conversation?.id || 'CREATE_NEW', {
                  messages: [...(conversation?.messages || []), voiceMessage]
                });
              }
            }}
          />
        )}
      </div>
      
      
      {/* Chat Windows */}
      {openChats.map((chat, index) => (
        <ChatWindow
          key={chat.id}
          chat={chat}
          onClose={handleCloseChat}
          onSendMessage={handleSendChatMessage}
          position={index}
        />
      ))}

      {/* Image Preview Modal for Welcome Screen */}
      {modalImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4"
          onClick={() => setModalImage(null)}
        >
          <div className="relative max-w-full max-h-full">
            <img 
              src={modalImage} 
              alt="Attachment preview" 
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setModalImage(null)}
              className="absolute top-4 right-4 p-2 bg-white rounded-lg shadow-lg hover:bg-gray-100 transition-colors"
              title="Close"
            >
              <X className="w-6 h-6 text-gray-700" />
            </button>
          </div>
        </div>
      )}
      
      {/* App Option Modals for Welcome Screen */}
      {showAppOptionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            {/* Email Thread Modal */}
            {showAppOptionModal?.type === 'emailThread' && (
              <>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-600" />
                  Email Thread Context
                </h3>
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="6"
                  placeholder="Paste the email thread or context here..."
                  value={appOptions.emailThread || ''}
                  onChange={(e) => setAppOptions({ ...appOptions, emailThread: e.target.value })}
                />
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => setShowAppOptionModal(null)}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setShowAppOptionModal(null);
                    }}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Save
                  </button>
                </div>
              </>
            )}
            
            {/* Tone Modal */}
            {showAppOptionModal?.type === 'tone' && (
              <>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Palette className="w-5 h-5 text-purple-600" />
                  Select Tone
                </h3>
                <div className="space-y-2">
                  {[
                    { value: 'professional', label: 'Professional' },
                    { value: 'friendly', label: 'Friendly' },
                    { value: 'formal', label: 'Formal' },
                    { value: 'casual', label: 'Casual' },
                    { value: 'enthusiastic', label: 'Enthusiastic' },
                    { value: 'empathetic', label: 'Empathetic' },
                    { value: 'persuasive', label: 'Persuasive' }
                  ].map((tone) => (
                    <button
                      key={tone.value}
                      onClick={() => {
                        setAppOptions({ ...appOptions, tone: tone.value });
                        setShowAppOptionModal(null);
                      }}
                      className={`w-full p-3 text-left rounded-lg border transition-colors ${
                        appOptions.tone === tone.value
                          ? 'border-purple-500 bg-purple-50 text-purple-700'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {tone.label}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setShowAppOptionModal(null)}
                  className="w-full mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </>
            )}
            
            {/* Purpose Modal */}
            {showAppOptionModal?.type === 'purpose' && (
              <>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-green-600" />
                  Select Purpose
                </h3>
                <div className="space-y-2">
                  {[
                    { value: 'general', label: 'General Communication' },
                    { value: 'request', label: 'Making a Request' },
                    { value: 'followup', label: 'Follow-up' },
                    { value: 'introduction', label: 'Introduction' },
                    { value: 'thank-you', label: 'Thank You' },
                    { value: 'apology', label: 'Apology' },
                    { value: 'announcement', label: 'Announcement' },
                    { value: 'invitation', label: 'Invitation' }
                  ].map((purpose) => (
                    <button
                      key={purpose.value}
                      onClick={() => {
                        setAppOptions({ ...appOptions, purpose: purpose.value });
                        setShowAppOptionModal(null);
                      }}
                      className={`w-full p-3 text-left rounded-lg border transition-colors ${
                        appOptions.purpose === purpose.value
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {purpose.label}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setShowAppOptionModal(null)}
                  className="w-full mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </>
            )}
            
            {/* Multiple Versions Modal */}
            {showAppOptionModal?.type === 'multipleVersions' && (
              <>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-600" />
                  Multiple Versions
                </h3>
                <p className="text-gray-600 mb-4">
                  Would you like me to generate multiple versions of your draft?
                </p>
                <div className="space-y-2">
                  <button
                    onClick={() => {
                      setAppOptions({ ...appOptions, multipleVersions: true });
                      setShowAppOptionModal(null);
                    }}
                    className={`w-full p-3 text-left rounded-lg border transition-colors ${
                      appOptions.multipleVersions
                        ? 'border-amber-500 bg-amber-50 text-amber-700'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    Yes, generate multiple versions
                  </button>
                  <button
                    onClick={() => {
                      setAppOptions({ ...appOptions, multipleVersions: false });
                      setShowAppOptionModal(null);
                    }}
                    className={`w-full p-3 text-left rounded-lg border transition-colors ${
                      !appOptions.multipleVersions
                        ? 'border-gray-500 bg-gray-50 text-gray-700'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    No, single version only
                  </button>
                </div>
                <button
                  onClick={() => setShowAppOptionModal(null)}
                  className="w-full mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </>
            )}
            
            {/* Competition Analyzer Modals */}
            {/* Your Product URL Modal */}
            {showAppOptionModal?.type === 'yourProduct' && (
              <>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-600" />
                  Your Website URL
                </h3>
                <p className="text-gray-600 mb-4">
                  Enter your website URL for competitive analysis
                </p>
                <input
                  type="url"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://yourwebsite.com"
                  value={appOptions.yourProduct || ''}
                  onChange={(e) => setAppOptions({ ...appOptions, yourProduct: e.target.value })}
                />
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => setShowAppOptionModal(null)}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setShowAppOptionModal(null)}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Save
                  </button>
                </div>
              </>
            )}
            
            {/* Competitors Modal */}
            {showAppOptionModal?.type === 'competitors' && (
              <>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-red-600" />
                  Competitor Websites
                </h3>
                <p className="text-gray-600 mb-4">
                  Enter competitor URLs (one per line)
                </p>
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  rows="5"
                  placeholder="https://competitor1.com&#10;https://competitor2.com&#10;https://competitor3.com"
                  value={appOptions.competitors || ''}
                  onChange={(e) => setAppOptions({ ...appOptions, competitors: e.target.value })}
                />
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => setShowAppOptionModal(null)}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setShowAppOptionModal(null)}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Save
                  </button>
                </div>
              </>
            )}
            
            {/* Keywords Modal */}
            {showAppOptionModal?.type === 'keywords' && (
              <>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Search className="w-5 h-5 text-purple-600" />
                  Focus Keywords
                </h3>
                <p className="text-gray-600 mb-4">
                  Enter key industry terms or focus areas (comma-separated)
                </p>
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  rows="3"
                  placeholder="e.g., project management, collaboration tools, remote work, productivity"
                  value={appOptions.keywords || ''}
                  onChange={(e) => setAppOptions({ ...appOptions, keywords: e.target.value })}
                />
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => setShowAppOptionModal(null)}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setShowAppOptionModal(null)}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Save
                  </button>
                </div>
              </>
            )}
            
            {/* Analysis Type Modal */}
            {showAppOptionModal?.type === 'analysisType' && (
              <>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <BarChart className="w-5 h-5 text-green-600" />
                  Analysis Type
                </h3>
                <p className="text-gray-600 mb-4">
                  What type of competitive analysis do you need?
                </p>
                <div className="space-y-2">
                  {[
                    { value: 'comprehensive', label: 'Comprehensive Analysis', desc: 'Full competitor overview' },
                    { value: 'features', label: 'Feature Comparison', desc: 'Compare product features' },
                    { value: 'pricing', label: 'Pricing Strategy', desc: 'Analyze pricing models' },
                    { value: 'marketing', label: 'Marketing & Positioning', desc: 'Marketing strategies' },
                    { value: 'strengths-weaknesses', label: 'SWOT Analysis', desc: 'Strengths & weaknesses' },
                    { value: 'market-share', label: 'Market Share & Trends', desc: 'Market positioning' }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setAppOptions({ ...appOptions, analysisType: type.value });
                        setShowAppOptionModal(null);
                      }}
                      className={`w-full p-3 text-left rounded-lg border transition-colors ${
                        appOptions.analysisType === type.value
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium">{type.label}</div>
                      <div className="text-sm text-gray-500">{type.desc}</div>
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setShowAppOptionModal(null)}
                  className="w-full mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* Credits Modal - Added for welcome screen */}
      <CreditsModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
        currentUser={currentUser}
        userCredits={userCredits}
        onUpdateCredits={handleUpdateCredits}
        canUseBoost={!currentUser?.hasUsedBoost}
      />
      
      {/* Out of Credits Modal - Added for welcome screen */}
      <OutOfCreditsModal
        isOpen={showOutOfCreditsModal}
        onClose={() => setShowOutOfCreditsModal(false)}
        onViewCredits={() => {
          setShowOutOfCreditsModal(false);
          setShowCreditsModal(true);
        }}
        currentUser={currentUser}
        onUpdateCredits={handleUpdateCredits}
        canUseBoost={!currentUser?.hasUsedBoost}
      />
    </div>
    );
  }

  // Ensure conversation has required properties (when conversation exists but is invalid)
  if (conversation && !conversation.id) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-claude-muted">Loading conversation...</p>
      </div>
    );
  }
  
  return (
    <div className="flex-1 flex h-full w-full min-w-0">
      {/* Main Chat Container */}
      <div 
        className="flex-1 flex flex-col h-full min-w-0 relative"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
      {/* Hidden File Input - placed early so ref is always available */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,application/pdf,.doc,.docx,.txt,.md,.csv,.xlsx"
        onChange={handleDirectFileSelection}
        style={{ display: 'none' }}
      />
      
      {isDragging && (
        <div className={`absolute inset-0 z-50 border-2 border-dashed flex items-center justify-center ${
          currentModelSupportsVision() 
            ? 'bg-blue-500 bg-opacity-20 border-blue-500' 
            : 'bg-red-500 bg-opacity-20 border-red-500'
        }`}>
          <div className="bg-white rounded-lg p-6 shadow-lg text-center max-w-md">
            {currentModelSupportsVision() ? (
              <>
                <div className="text-blue-600 text-2xl mb-2">📁</div>
                <div className="text-lg font-medium text-gray-800">Drop files here to upload</div>
                <div className="text-sm text-gray-600">Images and documents supported</div>
              </>
            ) : (
              <>
                <div className="text-red-600 text-2xl mb-2">🚫</div>
                <div className="text-lg font-medium text-gray-800">File upload not supported</div>
                <div className="text-sm text-gray-600 mb-3">
                  The current model "{selectedModel}" doesn't support file processing
                </div>
                <div className="text-sm text-blue-600 font-medium">
                  Switch to a vision model (GPT-4o, Claude, or Gemini) to upload files
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <div className={`flex-1 flex flex-col appearance-${appearanceMode} w-full min-w-0 ${
        isSuppleMode ? 'supple-card-frosted' : ''
      } ${
        selectedContextSpaceId && contextSpaceContext?.name?.toLowerCase().includes('test') ? 'border-0' : ''
      }`}>
        <header className={`px-6 py-2 flex items-center justify-between ${
          isSuppleMode 
            ? 'supple-card-glass supple-border-subtle backdrop-blur-md' 
            : ' border-claude-border bg-white'
        } ${
          selectedContextSpaceId && contextSpaceContext?.name?.toLowerCase().includes('test') ? 'border-0' : ''
        }`}>
          <div className="flex items-center flex-1 min-w-0">
            {/* Mobile hamburger button */}
            <button
              onClick={onToggleSidebar}
              className="p-2 mr-2 md:hidden hover:bg-gray-100 rounded-lg transition-colors"
              title="Toggle sidebar"
            >
              <Menu className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-sm md:text-lg font-semibold text-claude-text truncate flex-1 min-w-0">
              {conversation?.title || 'New Chat'}
            </h1>

            {/* Seasonal Theme Header */}
            <div className="ml-2 flex-shrink-0">
              <SeasonalHeaderDecoration />
            </div>

            {/* Context Space Indicator */}
            {selectedContextSpaceId && (
              <div className="ml-2 relative flex-shrink-0" data-space-dropdown>
                <button
                  onClick={() => setShowSpaceDropdown(!showSpaceDropdown)}
                  className="flex items-center gap-1 px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-full text-xs font-medium border border-blue-200 transition-colors relative"
                  data-tour="context-spaces"
                >
                  <FolderOpen className="w-3 h-3" />
                  <span>
                    {contextSpaceContext?.name || conversation?.contextSpaces?.[0]?.contextSpace?.name || 'Context Space'}
                  </span>
                  {(contextSpaceContext?.icon || conversation?.contextSpaces?.[0]?.contextSpace?.icon) && (
                    <span className="text-sm">
                      {contextSpaceContext?.icon || conversation?.contextSpaces?.[0]?.contextSpace?.icon}
                    </span>
                  )}
                  <ChevronDown className="w-3 h-3" />
                  <div className="absolute -top-1 -right-1">
                    <NewFeatureTag featureId="context-spaces" size="xs" />
                  </div>
                </button>
                
                {/* Space Dropdown */}
                {showSpaceDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    <div className="p-2">
                      <button
                        onClick={() => {
                          setShowContextSpaces && setShowContextSpaces(true);
                          setShowSpaceDropdown(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-2"
                      >
                        <FolderOpen className="w-4 h-4" />
                        Change Space
                      </button>
                      <button
                        onClick={() => {
                          onExitContextSpace && onExitContextSpace();
                          setShowSpaceDropdown(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-2"
                      >
                        <X className="w-4 h-4" />
                        Exit Space
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={handleShareConversation}
              className="ml-2 p-1.5 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              title="Share conversation"
            >
              <Share2 className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={() => onOpenMemory && onOpenMemory()}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              title="Memory - Set your AI preferences"
            >
              <Brain className="w-4 h-4 text-gray-600" />
            </button>
          </div>
          <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
            {/* New Chat button - mobile only */}
            <button
              onClick={onNewConversation}
              className="md:hidden p-2 bg-orange-500 hover:bg-orange-600 text-white rounded-full transition-colors"
              title="New chat"
            >
              <Plus className="w-4 h-4" />
            </button>
            
            {(userCredits || true) && (
              <div className={`flex items-center gap-2 text-sm ${
                userCredits ? (
                  userCredits && userCredits.creditsRemaining <= 0 ? 'text-red-600' :
                  userCredits.monthlyCredits && userCredits.creditsRemaining <= userCredits.monthlyCredits * 0.1 ? 'text-yellow-600' :
                  'text-gray-600'
                ) : 'text-gray-500'
              }`}>
                {isLargeScreen ? (
                  <button
                    onClick={() => setShowCreditsModal(true)}
                    className="flex items-center gap-2 px-2 py-1 hover:bg-gray-100 rounded-lg transition-colors"
                    title={userCredits && userCredits.monthlyCredits ? `View credits - ${Math.round(((userCredits.monthlyCredits - userCredits.creditsRemaining) / userCredits.monthlyCredits) * 100)}% used this month` : 'View credits - Loading...'}
                  >
                    <div className="relative">
                      {/* Circular progress border */}
                      <div className="relative w-6 h-6 flex items-center justify-center">
                        {userCredits && userCredits.monthlyCredits && (
                          <svg className="absolute inset-0 w-6 h-6 transform -rotate-90">
                            <circle
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="2"
                              fill="transparent"
                              className="text-gray-200"
                            />
                            <circle
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="2"
                              fill="transparent"
                              strokeDasharray={`${2 * Math.PI * 10}`}
                              strokeDashoffset={`${2 * Math.PI * 10 * (1 - Math.min(((userCredits.monthlyCredits - userCredits.creditsRemaining) / userCredits.monthlyCredits), 1))}`}
                              className={`transition-all duration-300 ${
                                userCredits.creditsRemaining <= 0 ? 'text-red-500' :
                                ((userCredits.monthlyCredits - userCredits.creditsRemaining) / userCredits.monthlyCredits) >= 0.7 ? 'text-orange-500' :
                                'text-green-500'
                              }`}
                              strokeLinecap="round"
                            />
                          </svg>
                        )}
                        {(!userCredits || !userCredits.monthlyCredits) && (
                          <svg className="absolute inset-0 w-6 h-6 transform -rotate-90">
                            <circle
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="2"
                              fill="transparent"
                              className="text-gray-300"
                            />
                          </svg>
                        )}
                        <CreditCard className="w-3 h-3 relative z-10" />
                      </div>
                    </div>
                    <span>{userCredits && userCredits.monthlyCredits ? `${userCredits.creditsRemaining.toLocaleString()} / ${userCredits.monthlyCredits.toLocaleString()}` : 'Loading...'}</span>
                  </button>
                ) : (
                  <button
                    onClick={() => setShowCreditsModal(true)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors relative"
                    title={userCredits && userCredits.monthlyCredits ? `View credits - ${Math.round(((userCredits.monthlyCredits - userCredits.creditsRemaining) / userCredits.monthlyCredits) * 100)}% used this month` : 'View credits - Loading...'}
                  >
                    {/* Circular progress border */}
                    <div className="relative w-6 h-6 flex items-center justify-center">
                      {userCredits && (
                        <svg className="absolute inset-0 w-6 h-6 transform -rotate-90">
                          <circle
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="2"
                            fill="transparent"
                            className="text-gray-200"
                          />
                          <circle
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="2"
                            fill="transparent"
                            strokeDasharray={`${2 * Math.PI * 10}`}
                            strokeDashoffset={`${2 * Math.PI * 10 * (1 - Math.min(((userCredits.monthlyCredits - userCredits.creditsRemaining) / userCredits.monthlyCredits), 1))}`}
                            className={`transition-all duration-300 ${
                              userCredits.creditsRemaining <= 0 ? 'text-red-500' :
                              ((userCredits.monthlyCredits - userCredits.creditsRemaining) / userCredits.monthlyCredits) >= 0.7 ? 'text-orange-500' :
                              'text-green-500'
                            }`}
                            strokeLinecap="round"
                          />
                        </svg>
                      )}
                      <CreditCard className="w-3 h-3 relative z-10" />
                    </div>
                  </button>
                )}
              </div>
            )}
          </div>
        </header>
        


      <div 
        className="flex-1 overflow-y-auto px-6 py-4 relative" 
        ref={messagesContainerRef}
        style={{ 
          maxHeight: selectedContextSpaceId && contextSpaceContext?.name?.toLowerCase().includes('test') 
            ? 'calc(100vh - 280px)' 
            : 'calc(100vh - 240px)' 
        }}
      >
        <div className="max-w-4xl mx-auto space-y-6">
          {/* App Options for App Conversations */}
          {isAppConversation && conversation?.metadata && (
            <AppOptions
              appId={conversation.metadata.appId}
              appName={conversation.metadata.appName || 'AI Assistant'}
              onOptionsChange={(options) => {
                setAppOptions(options);
                // Include options in the message metadata
              }}
              initialOptions={appOptions}
            />
          )}

          {/* System Prompt for App Conversations */}
          {isAppConversation && conversation?.metadata?.systemPrompt && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <button
                  onClick={() => setShowSystemPrompt(!showSystemPrompt)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg transition-colors"
                  title={showSystemPrompt ? "Hide system prompt" : "Show system prompt"}
                >
                  <Settings className="w-4 h-4" />
                  <span className="text-sm font-medium">System Prompt</span>
                  {showSystemPrompt ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
              </div>
              
              {showSystemPrompt && (
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-purple-600" />
                      <h3 className="font-medium text-purple-900">
                        {conversation.metadata.appName || 'AI Assistant'}
                      </h3>
                    </div>
                    <button
                      onClick={() => {
                        setEditingSystemPrompt(true);
                        setTempSystemPrompt(conversation.metadata.systemPrompt);
                      }}
                      className="p-1.5 rounded hover:bg-purple-100 text-purple-600"
                      title="Edit system prompt"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {editingSystemPrompt ? (
                    <div className="space-y-2">
                      <textarea
                        className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        rows="4"
                        value={tempSystemPrompt}
                        onChange={(e) => setTempSystemPrompt(e.target.value)}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            onUpdateConversation(conversation.id, {
                              metadata: {
                                ...conversation.metadata,
                                systemPrompt: tempSystemPrompt
                              }
                            });
                            setEditingSystemPrompt(false);
                          }}
                          className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingSystemPrompt(false);
                            setTempSystemPrompt('');
                          }}
                          className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-purple-800 whitespace-pre-wrap">
                      {conversation.metadata.systemPrompt}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          
          {conversation.messages?.map((msg, index) => (
            <div key={msg.id} className="flex gap-4">
              <div className="flex-shrink-0">
                {msg.role === 'user' ? (
                  <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                ) : (
                  <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-medium text-claude-text">
                    {msg.role === 'user' ? 'You' : msg.model || 'Assistant'}
                  </span>
                  <span className="text-xs text-claude-muted">
                    {msg.timestamp && new Date(msg.timestamp).toLocaleTimeString()}
                  </span>
                  {msg.edited && <span className="text-xs text-claude-muted">(edited)</span>}
                  {msg.stopped && <span className="text-xs text-orange-600">(stopped)</span>}
                  {/* Collapse/Expand button */}
                  {msg.content && msg.content.split('\n').length > 1 && (
                    <button
                      onClick={() => toggleMessageCollapse(msg.id)}
                      className="text-xs text-claude-muted hover:text-claude-text transition-colors"
                      title={collapsedMessages.has(msg.id) ? "Expand message" : "Collapse message"}
                    >
                      {collapsedMessages.has(msg.id) ? '▶ Expand' : '▼ Collapse'}
                    </button>
                  )}
                </div>
                {editingMessageId === msg.id ? (
                  <div className="space-y-2">
                    <textarea
                      ref={editTextareaRef}
                      value={editingContent}
                      onChange={(e) => setEditingContent(e.target.value)}
                      className="w-full resize-none border border-claude-border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-claude-accent"
                      rows={1}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSaveEdit(index)}
                        className="px-3 py-1 bg-claude-accent text-white rounded text-sm hover:bg-opacity-90"
                      >
                        Save & Send
                      </button>
                      <button
                        onClick={() => setEditingMessageId(null)}
                        className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : msg.role === 'user' ? (
                  <>
                    {msg.attachments && msg.attachments.length > 0 && (
                      <AttachmentDisplay 
                        attachments={msg.attachments} 
                        onImageClick={setModalImage} 
                      />
                    )}
                    <div className={`rounded-lg px-4 py-3 border ${
                      isSuppleMode 
                        ? 'supple-card-glass supple-border-subtle' 
                        : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className={`whitespace-pre-wrap break-words ${
                        isSuppleMode ? 'supple-text-primary' : 'text-gray-800'
                      }`}>
                        {collapsedMessages.has(msg.id) 
                          ? msg.content.split('\n')[0] + (msg.content.includes('\n') ? '...' : '')
                          : msg.content
                        }
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      {(msg.isVoiceMessage || msg.transcription) && (
                        <button
                          onClick={() => handlePlayVoiceMessage(msg)}
                          className="p-1.5 rounded hover:bg-green-100 text-green-500 hover:text-green-700"
                          title="Play voice message"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleCopyMessage(msg.content, msg.id)}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                        title="Copy message"
                      >
                        {copiedMessageId === msg.id ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleEditMessage(msg.id, msg.content)}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                        title="Edit message"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {msg.isBurstResponse ? (
                      <BurstResponse 
                        message={msg}
                        allModels={models}
                        onSelectResponse={handleSelectBurstResponse}
                        streamingResponses={burstResponses}
                      />
                    ) : msg.isRankBotResponse ? (
                      <RankBot
                        command={msg.commandId}
                        formData={msg.commandData}
                        messageId={msg.id}
                        onComplete={(rankData) => handleRankBotComplete(msg.id, rankData)}
                        onSendMessage={handleSendMessage}
                        storedProjectData={msg.projectData}
                        storedPositionData={msg.positionData}
                        isStoredComplete={msg.isComplete}
                      />
                    ) : (
                      (() => {
                        // Parse tokens if message doesn't have metadata
                        // Use StreamingMessageRenderer for all assistant messages
                        // This handles MCP tokens from database and web search tokens
                        return (
                          <StreamingMessageRenderer
                            content={collapsedMessages.has(msg.id)
                              ? msg.content.split('\n')[0] + (msg.content.includes('\n') ? '...' : '')
                              : msg.content
                            }
                            streamingTokens={[]}
                            isStreaming={false}
                            model={msg.model}
                          />
                        );
                      })()
                    )}
                    
                    {/* Voice Message Controls for Assistant Messages */}
                    {msg.role === 'assistant' && msg.content && !msg.progressStatus && (
                      <div className="mt-2 mb-2">
                        <VoiceMessage 
                          message={msg}
                          selectedVoice={selectedVoice}
                          autoPlay={autoPlayVoice}
                        />
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2 mt-2">
                      {msg.role === 'assistant' && !msg.isBurstResponse ? (
                        <>
                          {(msg.isVoiceMessage || msg.transcription) && (
                            <button
                              onClick={() => handlePlayVoiceMessage(msg)}
                              className="p-1.5 rounded hover:bg-green-100 text-green-500 hover:text-green-700"
                              title="Play voice message"
                            >
                              <Play className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleCopyMessage(msg.content, msg.id)}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                            title="Copy message"
                          >
                            {copiedMessageId === msg.id ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                          {msg.isError && msg.canRegenerate && (
                            <button
                              onClick={() => handleRegenerateMessage(msg)}
                              className="p-1.5 rounded hover:bg-red-100 text-red-500 hover:text-red-700"
                              title="Regenerate response"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </button>
                          )}
                          <div className="relative resend-dropdown-container">
                            <button
                              onClick={() => handleResendMessage(index)}
                              className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                              title="Resend message"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setResendDropdownId(resendDropdownId === msg.id ? null : msg.id)}
                              className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                              title="Resend with different model"
                            >
                              <ChevronDown className="w-4 h-4" />
                            </button>
                            {resendDropdownId === msg.id && (
                              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-10 max-h-64 overflow-y-auto">
                                {filteredModels.map(([provider, modelList]) => (
                                  <div key={provider}>
                                    <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase">
                                      {provider}
                                    </div>
                                    {(modelList || []).map(model => (
                                      <button
                                        key={model.id}
                                        onClick={() => handleResendMessage(index, model.id)}
                                        className="w-full text-left px-3 py-1.5 hover:bg-gray-100 text-sm"
                                      >
                                        {model.name}
                                      </button>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          {/* Show token and cost info for admins */}
                          {currentUser?.isAdmin && msg.role === 'assistant' && (msg.tokensUsed || msg.tokenCount) && (
                            <div className="text-xs text-gray-500 flex items-center gap-3">
                              <span title="Tokens used">
                                📊 {(msg.tokensUsed || msg.tokenCount).toLocaleString()} tokens
                              </span>
                              {msg.cost && (
                                <span title="Cost" className="text-green-600">
                                  💵 ${msg.cost.toFixed(4)}
                                </span>
                              )}
                            </div>
                          )}
                        </>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}

          {isStreaming && !burstMode && (
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-medium text-claude-text">{selectedModel}</span>
                  {streamingTokens.length === 0 && (
                    <span className="text-xs text-claude-muted">typing...</span>
                  )}
                </div>
                <StreamingMessageRenderer 
                  content={typeof streamingMessage === 'object' ? streamingMessage.content : streamingMessage} 
                  streamingTokens={streamingTokens}
                  isStreaming={true}
                  model={selectedModel}
                  onCitationClick={handleCitationClick}
                  contextSpaceId={selectedContextSpaceId}
                />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Scroll to bottom overlay - positioned between messages and input */}
      {isUserScrolledUp && (
        <div className="fixed bottom-24 left-1/2 transform -translate-x-1/2 z-50">
          <button
            onClick={() => scrollToBottom(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-full shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 animate-pulse"
            title="Scroll to bottom - New messages available"
          >
            <ArrowDown className="w-4 h-4" />
            <span className="text-sm font-medium">New messages</span>
          </button>
        </div>
      )}
      
     

      {/* Unified Input Area */}
      <div className="px-6 py-4">
        <div className="max-w-4xl mx-auto">
          {/* Test Space Indicator - Compact */}
          {selectedContextSpaceId && contextSpaceContext?.name?.toLowerCase().includes('test') && (
            <div className={`mb-2 p-2 rounded-md text-xs text-center ${
              isSuppleMode 
                ? 'supple-card-glass supple-border-subtle backdrop-blur-md supple-text-primary' 
                : 'bg-blue-50 border border-blue-200 text-blue-700'
            }`}>
              <div className="flex items-center justify-center gap-1.5">
                <FolderOpen className="w-3 h-3" />
                <span>You are in the {contextSpaceContext.name} space</span>
              </div>
            </div>
          )}
          {/* Main Input Container - Rounded border box matching chat width */}
          <div className="border border-gray-300 rounded-2xl p-4 bg-white shadow-sm mb-3">
            {/* AI App Selector */}
            {selectedApp && (
              <div className="mb-3 pb-3 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`${selectedApp.color || 'bg-purple-500'} p-2 rounded-lg text-white`}>
                      {selectedApp.icon && <selectedApp.icon className="w-4 h-4" />}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{selectedApp.name}</div>
                      <div className="text-xs text-gray-500">{selectedApp.description}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedApp(null);
                      setAppOptions({});
                      setConversationApp(null);
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Remove
                  </button>
                </div>
                
                {/* App Options Buttons */}
                {selectedApp.id === 'draft-composer' && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowAppOptionModal({ type: 'emailThread', show: true });
                      }}
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs transition-colors ${
                        appOptions.emailThread 
                          ? 'bg-blue-50 text-blue-700 hover:bg-blue-100' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <Mail className="w-3 h-3" />
                      {appOptions.emailThread ? 'Thread added' : 'Add thread'}
                    </button>
                    
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowAppOptionModal({ type: 'tone', show: true });
                      }}
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs transition-colors ${
                        appOptions.tone
                          ? 'bg-purple-50 text-purple-700 hover:bg-purple-100' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <Palette className="w-3 h-3" />
                      {appOptions.tone ? appOptions.tone.charAt(0).toUpperCase() + appOptions.tone.slice(1) : 'Set tone'}
                    </button>
                    
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowAppOptionModal({ type: 'purpose', show: true });
                      }}
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs transition-colors ${
                        appOptions.purpose
                          ? 'bg-green-50 text-green-700 hover:bg-green-100' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <Target className="w-3 h-3" />
                      {appOptions.purpose 
                        ? appOptions.purpose.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) 
                        : 'Set purpose'}
                    </button>
                    
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowAppOptionModal({ type: 'multipleVersions', show: true });
                      }}
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs transition-colors ${
                        appOptions.multipleVersions
                          ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <Sparkles className="w-3 h-3" />
                      {appOptions.multipleVersions ? 'Multiple versions' : 'Versions'}
                    </button>
                    
                    {/* System Prompt Indicator */}
                    <div className="inline-flex items-center gap-1 px-3 py-1 bg-gray-50 text-gray-600 rounded-md text-xs">
                      <Settings className="w-3 h-3" />
                      System prompt active
                    </div>
                  </div>
                )}
                
                {selectedApp.id === 'competitor-analyzer' && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowAppOptionModal({ type: 'yourProduct', show: true });
                      }}
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs transition-colors ${
                        appOptions.yourProduct 
                          ? 'bg-blue-50 text-blue-700 hover:bg-blue-100' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <Globe className="w-3 h-3" />
                      {appOptions.yourProduct ? 'Your URL set' : 'Add your URL'}
                    </button>
                    
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowAppOptionModal({ type: 'competitors', show: true });
                      }}
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs transition-colors ${
                        appOptions.competitors
                          ? 'bg-red-50 text-red-700 hover:bg-red-100' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <TrendingUp className="w-3 h-3" />
                      {appOptions.competitors ? `${appOptions.competitors.split(',').length} competitors` : 'Add competitors'}
                    </button>
                    
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowAppOptionModal({ type: 'keywords', show: true });
                      }}
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs transition-colors ${
                        appOptions.keywords
                          ? 'bg-purple-50 text-purple-700 hover:bg-purple-100' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <Search className="w-3 h-3" />
                      {appOptions.keywords ? 'Keywords set' : 'Set keywords'}
                    </button>
                    
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowAppOptionModal({ type: 'analysisType', show: true });
                      }}
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs transition-colors ${
                        appOptions.analysisType
                          ? 'bg-green-50 text-green-700 hover:bg-green-100' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <BarChart className="w-3 h-3" />
                      {appOptions.analysisType 
                        ? appOptions.analysisType.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) 
                        : 'Analysis type'}
                    </button>
                    
                    {/* System Prompt Indicator */}
                    <div className="inline-flex items-center gap-1 px-3 py-1 bg-gray-50 text-gray-600 rounded-md text-xs">
                      <Settings className="w-3 h-3" />
                      System prompt active
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Uploaded Files Display */}
            {attachedFiles.length > 0 && (
              <div className="mb-3 p-3 bg-gray-50 rounded-lg border">
                <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Attached Files ({attachedFiles.length})
                </div>
                <div className="space-y-2">
                  {attachedFiles.map((file) => (
                    <div key={file.fileId} className="flex items-center justify-between p-2 bg-white rounded border">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {file.isImage ? (
                          <ImageIcon className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        ) : (
                          <FileText className="w-4 h-4 text-gray-600 flex-shrink-0" />
                        )}
                        <span className="text-sm text-gray-900 truncate" title={file.name || file.filename}>
                          {file.name || file.filename}
                        </span>
                        <span className="text-xs text-gray-500 flex-shrink-0">
                          {file.size < 1024 ? `${file.size} B` : 
                           file.size < 1024 * 1024 ? `${(file.size / 1024).toFixed(1)} KB` : 
                           `${(file.size / (1024 * 1024)).toFixed(1)} MB`}
                        </span>
                      </div>
                      <button
                        onClick={() => removeAttachedFile(file.fileId)}
                        className="ml-2 p-1 text-gray-400 hover:text-red-600 flex-shrink-0"
                        title="Remove file"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Credit Warning Banner */}
            {(userCredits || true) && (
              (() => {
                const usagePercentage = userCredits && userCredits.monthlyCredits ? ((userCredits.monthlyCredits - userCredits.creditsRemaining) / userCredits.monthlyCredits) * 100 : 0;
                const isOutOfCredits = userCredits && userCredits.creditsRemaining <= 0;
                const isApproachingLimit = usagePercentage >= 80 && !isOutOfCredits;
                
                if (isOutOfCredits) {
                  return (
                    <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-red-600" />
                          <span className="text-sm font-medium text-red-800">
                            Credit limit reached. Contact administrator.
                          </span>
                        </div>
                        {!currentUser?.hasUsedBoost && (
                          <button
                            onClick={async () => {
                              try {
                                await handleUpdateCredits({
                                  action: 'topup',
                                  amount: 10000,
                                  userId: currentUser?.id
                                });
                              } catch (error) {
                                console.error('Failed to add grace credits:', error);
                              }
                            }}
                            className="px-3 py-1 bg-purple-600 text-white text-xs rounded-md hover:bg-purple-700 transition-colors"
                          >
                            Borrow 10K Grace Credits
                          </button>
                        )}
                      </div>
                    </div>
                  );
                } else if (isApproachingLimit) {
                  return (
                    <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-yellow-600" />
                        <span className="text-sm text-yellow-800">
                          You are approaching your credit limit ({Math.round(usagePercentage)}% used)
                        </span>
                      </div>
                    </div>
                  );
                }
                return null;
              })()
            )}

            {/* Textarea container with proper upward expansion */}
            <div className="relative mb-3 flex flex-col justify-end" style={{ minHeight: '44px' }}>
              <textarea
                ref={textareaRef}
                value={message}
                onChange={handleMessageChange}
                onKeyDown={handleKeyPress}
                placeholder="Type your message..."
                disabled={isLoading}
                className="w-full resize-none border-none outline-none text-base disabled:opacity-50 disabled:cursor-not-allowed bg-transparent"
                rows={1}
                style={{
                  minHeight: '44px',
                  maxHeight: '88px',
                  resize: 'none'
                }}
              />
              <SlashCommands
                message={message}
                onSlashCommand={handleSlashCommand}
                onMessageChange={setMessage}
                showCommands={message.startsWith('/')}
                onToggleCommands={() => setShowSlashCommands(!showSlashCommands)}
                isVisible={true}
              />
            </div>

            {/* Bottom Controls Row */}
            <div className="flex items-center justify-between gap-2 text-sm border-gray-100 pt-3">
              {/* Left: Options Dropdown */}
              <div className="relative" ref={welcomeOptionsRef}>
                <button
                  onClick={() => setShowWelcomeOptionsDropdown(!showWelcomeOptionsDropdown)}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg transition-colors text-xs font-medium"
                  title="Options"
                >
                  <Plus className="w-4 h-4" />
                  Options
                </button>

                {/* Comprehensive Options Dropdown */}
                {showWelcomeOptionsDropdown && (
                  <div className="absolute bottom-full left-0 mb-2 w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-1">
                    <div className="space-y-1">
                      {/* Mode Section */}
                      <div className="p-2">
                        <div className="text-xs font-medium text-gray-500 mb-2">Mode</div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => {
                              setEnableAutoTools(true);
                            }}
                            className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                              enableAutoTools
                                ? 'bg-purple-100 text-purple-700 border border-purple-200'
                                : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            <Zap className="w-4 h-4" />
                            Auto
                          </button>
                          <button
                            onClick={() => {
                              setEnableAutoTools(false);
                              // Close other dropdowns first
                              setShowAIAppsDropdown(false);
                              setShowSlashCommandsDropdown(false);
                              setShowVoiceDropdown(false);
                              // Then open Manual Tools dropdown
                              setShowManualToolsDropdown(true);
                            }}
                            className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                              !enableAutoTools
                                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                                : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            <Settings className="w-4 h-4" />
                            Manual
                            {!enableAutoTools && enabledTools.length > 0 && (
                              <span className="ml-1 bg-blue-200 text-blue-700 text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                {enabledTools.length}
                              </span>
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="border-t border-gray-100"></div>

                      {/* AI Apps */}
                      <div className="relative">
                        <button
                          onClick={() => {
                            // Close other dropdowns first
                            setShowVoiceDropdown(false);
                            setShowSlashCommandsDropdown(false);
                            setShowManualToolsDropdown(false);
                            // Then open AI Apps dropdown
                            setShowAIAppsDropdown(true);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg text-sm"
                        >
                          <Sparkles className="w-4 h-4 text-purple-600" />
                          <span>AI Apps</span>
                          {selectedApp && (
                            <span className="ml-auto text-xs text-gray-500">({selectedApp.name})</span>
                          )}
                          <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
                        </button>

                        {/* AI Apps Dropdown - Opens to the right of this button */}
                        {showAIAppsDropdown && (
                          <div className="absolute top-0 left-full ml-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-50">
                            <div className="text-xs font-medium text-gray-500 mb-2 px-2">Select AI Assistant</div>
                            <div className="max-h-80 overflow-y-auto space-y-1">
                              {availableApps.map((app) => (
                                <button
                                  key={app.id}
                                  onClick={() => {
                                    setSelectedApp(app);
                                    setConversationApp({
                                      appId: app.id,
                                      appName: app.name,
                                      systemPrompt: app.systemPrompt,
                                      icon: undefined,
                                      color: app.color,
                                      description: app.description
                                    });
                                    setShowAIAppsDropdown(false);
                                    setShowWelcomeOptionsDropdown(false);
                                    setAppOptions({});
                                  }}
                                  className={`w-full flex items-center gap-2 p-2 rounded-lg text-left transition-colors ${
                                    selectedApp?.id === app.id
                                      ? 'bg-purple-50 text-purple-700 border border-purple-200'
                                      : 'hover:bg-gray-50 text-gray-700'
                                  }`}
                                >
                                  <div className={`${app.color} p-1.5 rounded text-white flex-shrink-0`}>
                                    <app.icon className="w-3 h-3" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium truncate">{app.name}</div>
                                    <div className="text-xs opacity-75 truncate">{app.description}</div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Slash Commands */}
                      <div className="relative">
                        <button
                          onClick={() => {
                            // Close other dropdowns first
                            setShowAIAppsDropdown(false);
                            setShowVoiceDropdown(false);
                            setShowManualToolsDropdown(false);
                            // Then open Slash Commands dropdown
                            setShowSlashCommandsDropdown(true);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg text-sm"
                        >
                          <Slash className="w-4 h-4 text-blue-600" />
                          <span>Slash Commands</span>
                          <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
                        </button>

                        {/* Slash Commands Dropdown - Opens to the right of this button */}
                        {showSlashCommandsDropdown && (
                          <div className="absolute top-0 left-full ml-2 bg-white border border-gray-200 rounded-lg shadow-lg p-2 w-64 z-50">
                            <div className="text-xs font-medium text-gray-500 mb-2 px-2">Slash Commands</div>
                            <div className="space-y-1">
                              {[
                                { command: '/rank', desc: 'Check SEO ranking positions', icon: '📈' },
                                { command: '/ads', desc: 'Analyze advertisement data', icon: '🎯' },
                                { command: '/analytics', desc: 'View website analytics', icon: '📊' },
                                { command: '/calls', desc: 'Call tracking data', icon: '📞' },
                                { command: '/audit', desc: 'SEO audit report', icon: '🔍' }
                              ].map((item) => (
                                <button
                                  key={item.command}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMessage(prev => prev + item.command + ' ');
                                    setShowSlashCommandsDropdown(false);
                                    setShowWelcomeOptionsDropdown(false);
                                  }}
                                  className="w-full text-left px-3 py-2 text-sm rounded transition-colors hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <span className="text-base">{item.icon}</span>
                                  <div>
                                    <div className="font-medium text-gray-900">{item.command}</div>
                                    <div className="text-xs text-gray-500">{item.desc}</div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Add Files & Photo */}
                      <button
                        onClick={() => {
                          setShowWelcomeOptionsDropdown(false);
                          triggerFileInput();
                        }}
                        disabled={!currentModelSupportsVision()}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${
                          currentModelSupportsVision()
                            ? 'hover:bg-gray-50 text-gray-700'
                            : 'text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        <Paperclip className="w-4 h-4 text-green-600" />
                        <span>Add Files & Photo</span>
                        {attachedFiles.length > 0 && (
                          <span className="ml-auto bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                            {attachedFiles.length}
                          </span>
                        )}
                      </button>

                      {/* AI Voices */}
                      <div className="relative">
                        <button
                          onClick={() => {
                            // Close other dropdowns first
                            setShowAIAppsDropdown(false);
                            setShowSlashCommandsDropdown(false);
                            setShowManualToolsDropdown(false);
                            // Then open Voice dropdown
                            setShowVoiceDropdown(true);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg text-sm"
                        >
                          <Mic className="w-4 h-4 text-indigo-600" />
                          <span>AI Voices</span>
                          <span className="ml-auto text-xs text-gray-500">
                            {selectedVoice.charAt(0).toUpperCase() + selectedVoice.slice(1)}
                          </span>
                          <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
                        </button>

                        {/* Voice Selection Dropdown - Opens to the right of this button */}
                        {showVoiceDropdown && (
                          <div ref={voiceDropdownRef} className="voice-dropdown absolute top-0 left-full ml-2 bg-white border border-gray-200 rounded-lg shadow-lg p-2 w-48 z-50" data-dropdown="voice">
                            <div className="text-xs font-medium text-gray-500 mb-2 px-2">Select Voice</div>
                            <div className="space-y-1">
                              {['alloy', 'echo', 'shimmer', 'sage', 'ballad', 'coral', 'ash', 'verse'].map((voice) => (
                                <button
                                  key={voice}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedVoice(voice);
                                    setShowVoiceDropdown(false);
                                  }}
                                  className={`w-full text-left px-3 py-2 text-sm rounded transition-colors ${
                                    selectedVoice === voice
                                      ? 'bg-blue-50 text-blue-700'
                                      : 'text-gray-700 hover:bg-gray-50'
                                  }`}
                                >
                                  {voice.charAt(0).toUpperCase() + voice.slice(1)}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Reasoning Effort - Only for supported models */}
                      {selectedModel?.includes('gpt-5') && (
                        <div className="px-3 py-2">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-gray-700 flex items-center gap-2">
                              <Brain className="w-4 h-4 text-purple-600" />
                              Reasoning Effort
                            </span>
                          </div>
                          <select
                            value={gpt5ReasoningEffort}
                            onChange={(e) => setGpt5ReasoningEffort(e.target.value)}
                            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="minimal">Minimal</option>
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                          </select>
                        </div>
                      )}

                      {/* Verbosity - Only for supported models */}
                      {selectedModel?.includes('gpt-5') && (
                        <div className="px-3 py-2">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-gray-700 flex items-center gap-2">
                              <MessageSquare className="w-4 h-4 text-orange-600" />
                              Verbosity
                            </span>
                          </div>
                          <select
                            value={gpt5Verbosity}
                            onChange={(e) => setGpt5Verbosity(e.target.value)}
                            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                          </select>
                        </div>
                      )}

                      {/* 1M Context Toggle - Only for Claude Sonnet 4.5 */}
                      {supports1MContext(selectedModel) && (
                        <div className="px-3 py-2 border-t border-gray-200">
                          <label className="flex items-center justify-between cursor-pointer group">
                            <div className="flex items-center gap-2">
                              <Database className="w-4 h-4 text-blue-600" />
                              <span className="text-sm text-gray-700 group-hover:text-gray-900">
                                1M Context Window
                              </span>
                              <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">BETA</span>
                            </div>
                            <input
                              type="checkbox"
                              checked={use1MContext}
                              onChange={(e) => setUse1MContext(e.target.checked)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            />
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Manual Tools Dropdown - Opens to the right */}
                {!enableAutoTools && showManualToolsDropdown && (
                  <div className="absolute bottom-0 left-full ml-2 w-72 p-3 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    <div className="text-xs font-medium text-gray-500 mb-2">Select Tools</div>
                    <div className="grid grid-cols-2 gap-1">
                      {availableTools.map(tool => {
                        const Icon = tool.icon;
                        const isEnabled = enabledTools.includes(tool.id);
                        return (
                          <button
                            key={tool.id}
                            onClick={() => toggleTool(tool.id)}
                            className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-xs transition-all ${
                              isEnabled
                                ? `${tool.bgColor} ${tool.textColor} border border-opacity-50`
                                : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                            }`}
                            title={tool.description}
                          >
                            <Icon className="w-3 h-3" />
                            <span className="font-medium">{tool.name}</span>
                            {isEnabled && (
                              <CheckCircle className="w-2 h-2 ml-auto" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

              </div>

              {/* Right: Model Selection, MCP, and Settings */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="min-w-[120px] sm:min-w-[160px] relative" data-tour="model-dropdown">
                  <ModelSelector
                    models={models}
                    selectedModel={selectedModel}
                    onModelChange={setSelectedModel}
                    burstGroups={modelGroups}
                    selectedBurstGroup={selectedBurstGroup}
                    onBurstGroupChange={setSelectedBurstGroup}
                    burstMode={burstMode}
                    onModeChange={setBurstMode}
                    topModelsConfig={topModelsConfig}
                  />
                  <div className="absolute -top-1 -right-1 z-10">
                    <NewFeatureTag size="xs" />
                  </div>
                </div>

                {/* Context Window Indicator */}
                {conversation?.id && (
                  <ContextWindowIndicator
                    conversation={conversation}
                    selectedModel={selectedModel || 'claude-sonnet-4'}
                    use1MContext={use1MContext}
                    onCompact={(result) => {
                      console.log('Context compacted:', result);
                      // Optionally reload conversation to get updated messages
                      if (conversation?.id) {
                        // Trigger a re-fetch or update local state
                      }
                    }}
                  />
                )}

                {/* MCP Button */}
                <MCPSelector button={true} />

                {/* Send Button */}
                <button
                  data-send-button
                  onClick={() => isStreaming ? handleStopResponse() : handleSendMessage()}
                  disabled={(!message.trim() && !isStreaming) || (isLoading && !isStreaming)}
                  className={`disabled:opacity-50 disabled:cursor-not-allowed p-2.5 rounded-lg transition-colors ${
                    isStreaming
                      ? 'bg-red-500 hover:bg-red-600 text-white'
                      : 'bg-orange-500 hover:bg-orange-600 text-white shadow-sm'
                  }`}
                  title={isStreaming ? 'Stop' : 'Send'}
                >
                  {isStreaming ? (
                    <Square className="w-4 h-4" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    
    {/* Chat Windows */}
    {openChats.map((chat, index) => (
      <ChatWindow
        key={chat.id}
        chat={chat}
        onClose={handleCloseChat}
        onSendMessage={handleSendChatMessage}
        onNewMessage={handleNewMessage}
        position={index}
      />
    ))}
    
    {/* Slash Commands Modal */}
    {showSlashCommands && (
      <SlashCommands
        message=""
        onSlashCommand={(commandId, data) => {
          handleSlashCommand(commandId, data);
          setShowSlashCommands(false);
        }}
        onMessageChange={() => {}}
        showCommands={true}
        onToggleCommands={() => setShowSlashCommands(false)}
        isVisible={true}
      />
    )}
    
    {/* Credits Modal */}
    <CreditsModal 
      isOpen={showCreditsModal}
      onClose={() => setShowCreditsModal(false)}
      userCredits={userCredits}
      onUpdateCredits={handleUpdateCredits}
    />
    
    {/* Out of Credits Modal */}
    <OutOfCreditsModal
      isOpen={showOutOfCreditsModal}
      onClose={() => setShowOutOfCreditsModal(false)}
      onViewCredits={() => {
        setShowOutOfCreditsModal(false);
        setShowCreditsModal(true);
      }}
      currentUser={currentUser}
      onUpdateCredits={handleUpdateCredits}
      canUseBoost={!currentUser?.hasUsedBoost} // Track if user has used their one-time boost
    />
    
    {/* AI Apps Modal */}
    {showAIApps && (
      <AIApps 
        onSelectApp={handleSelectAIApp}
        onClose={() => setShowAIApps(false)}
        isFromWelcome={!conversation.messages || conversation.messages.length === 0}
      />
    )}
    
    {/* App Option Modals */}
    {showAppOptionModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
          {/* Email Thread Modal */}
          {showAppOptionModal?.type === 'emailThread' && (
            <>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600" />
                Email Thread Context
              </h3>
              <textarea
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows="6"
                placeholder="Paste the email thread or context here..."
                value={appOptions.emailThread || ''}
                onChange={(e) => setAppOptions({ ...appOptions, emailThread: e.target.value })}
              />
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => setShowAppOptionModal(null)}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowAppOptionModal(null);
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Save
                </button>
              </div>
            </>
          )}
          
          {/* Tone Modal */}
          {showAppOptionModal?.type === 'tone' && (
            <>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Palette className="w-5 h-5 text-purple-600" />
                Select Tone
              </h3>
              <div className="space-y-2">
                {[
                  { value: 'professional', label: 'Professional' },
                  { value: 'friendly', label: 'Friendly' },
                  { value: 'formal', label: 'Formal' },
                  { value: 'casual', label: 'Casual' },
                  { value: 'enthusiastic', label: 'Enthusiastic' },
                  { value: 'empathetic', label: 'Empathetic' },
                  { value: 'persuasive', label: 'Persuasive' }
                ].map((tone) => (
                  <button
                    key={tone.value}
                    onClick={() => {
                      setAppOptions({ ...appOptions, tone: tone.value });
                      setShowAppOptionModal(null);
                    }}
                    className={`w-full p-3 text-left rounded-lg border transition-colors ${
                      appOptions.tone === tone.value
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {tone.label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowAppOptionModal(null)}
                className="w-full mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </>
          )}
          
          {/* Purpose Modal */}
          {showAppOptionModal?.type === 'purpose' && (
            <>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-green-600" />
                Select Purpose
              </h3>
              <div className="space-y-2">
                {[
                  { value: 'general', label: 'General Communication' },
                  { value: 'request', label: 'Making a Request' },
                  { value: 'followup', label: 'Follow-up' },
                  { value: 'introduction', label: 'Introduction' },
                  { value: 'thank-you', label: 'Thank You' },
                  { value: 'apology', label: 'Apology' },
                  { value: 'announcement', label: 'Announcement' },
                  { value: 'invitation', label: 'Invitation' }
                ].map((purpose) => (
                  <button
                    key={purpose.value}
                    onClick={() => {
                      setAppOptions({ ...appOptions, purpose: purpose.value });
                      setShowAppOptionModal(null);
                    }}
                    className={`w-full p-3 text-left rounded-lg border transition-colors ${
                      appOptions.purpose === purpose.value
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {purpose.label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowAppOptionModal(null)}
                className="w-full mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </>
          )}
          
          {/* Multiple Versions Modal */}
          {showAppOptionModal?.type === 'multipleVersions' && (
            <>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-600" />
                Multiple Versions
              </h3>
              <p className="text-gray-600 mb-4">
                Would you like me to generate multiple versions of your draft?
              </p>
              <div className="space-y-2">
                <button
                  onClick={() => {
                    setAppOptions({ ...appOptions, multipleVersions: true });
                    setShowAppOptionModal(null);
                  }}
                  className={`w-full p-3 text-left rounded-lg border transition-colors ${
                    appOptions.multipleVersions
                      ? 'border-amber-500 bg-amber-50 text-amber-700'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  Yes, generate multiple versions
                </button>
                <button
                  onClick={() => {
                    setAppOptions({ ...appOptions, multipleVersions: false });
                    setShowAppOptionModal(null);
                  }}
                  className={`w-full p-3 text-left rounded-lg border transition-colors ${
                    !appOptions.multipleVersions
                      ? 'border-gray-500 bg-gray-50 text-gray-700'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  No, single version only
                </button>
              </div>
              <button
                onClick={() => setShowAppOptionModal(null)}
                className="w-full mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </>
          )}
          
          {/* Competition Analyzer - Your Product URL Modal */}
          {showAppOptionModal?.type === 'yourProduct' && (
            <>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-600" />
                Your Product URL
              </h3>
              <input
                type="url"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://yourproduct.com"
                value={appOptions.yourProduct || ''}
                onChange={(e) => setAppOptions({ ...appOptions, yourProduct: e.target.value })}
              />
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => setShowAppOptionModal(null)}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowAppOptionModal(null);
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Save
                </button>
              </div>
            </>
          )}
          
          {/* Competition Analyzer - Competitors Modal */}
          {showAppOptionModal?.type === 'competitors' && (
            <>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-600" />
                Your Competitors
              </h3>
              <textarea
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                rows="4"
                placeholder="Enter competitor URLs or names, one per line..."
                value={appOptions.competitors || ''}
                onChange={(e) => setAppOptions({ ...appOptions, competitors: e.target.value })}
              />
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => setShowAppOptionModal(null)}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowAppOptionModal(null);
                  }}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Save
                </button>
              </div>
            </>
          )}
          
          {/* Competition Analyzer - Keywords Modal */}
          {showAppOptionModal?.type === 'keywords' && (
            <>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Search className="w-5 h-5 text-green-600" />
                Target Keywords
              </h3>
              <textarea
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                rows="3"
                placeholder="Enter keywords to analyze (e.g., productivity tool, project management)..."
                value={appOptions.keywords || ''}
                onChange={(e) => setAppOptions({ ...appOptions, keywords: e.target.value })}
              />
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => setShowAppOptionModal(null)}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowAppOptionModal(null);
                  }}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Save
                </button>
              </div>
            </>
          )}
          
          {/* Competition Analyzer - Analysis Type Modal */}
          {showAppOptionModal?.type === 'analysisType' && (
            <>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <BarChart className="w-5 h-5 text-amber-600" />
                Analysis Type
              </h3>
              <div className="space-y-2">
                {[
                  { value: 'comprehensive', label: 'Comprehensive Analysis' },
                  { value: 'features', label: 'Feature Comparison' },
                  { value: 'pricing', label: 'Pricing Strategy' },
                  { value: 'marketing', label: 'Marketing & Positioning' },
                  { value: 'strengths-weaknesses', label: 'SWOT Analysis' },
                  { value: 'market-share', label: 'Market Share & Trends' }
                ].map((type) => (
                  <button
                    key={type.value}
                    onClick={() => {
                      setAppOptions({ ...appOptions, analysisType: type.value });
                      setShowAppOptionModal(null);
                    }}
                    className={`w-full p-3 text-left rounded-lg border transition-colors ${
                      appOptions.analysisType === type.value
                        ? 'border-amber-500 bg-amber-50 text-amber-700'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowAppOptionModal(null)}
                className="w-full mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    )}
    
    {/* Image Preview Modal */}
    {modalImage && (
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4"
        onClick={() => setModalImage(null)}
      >
        <div className="relative max-w-full max-h-full">
          <img 
            src={modalImage} 
            alt="Attachment preview" 
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setModalImage(null)}
            className="absolute top-4 right-4 p-2 bg-white rounded-lg shadow-lg hover:bg-gray-100 transition-colors"
            title="Close"
          >
            <X className="w-6 h-6 text-gray-700" />
          </button>
        </div>
      </div>
    )}
    
    {/* AI Apps Page Modal */}
    {showAIApps && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
          <AppsPage onBack={() => setShowAIApps(false)} />
        </div>
      </div>
    )}


    {/* Real-time Audio Conversation Overlay */}
    {showAudioConversation && (
      <RealtimeAudioOverlay
        onClose={() => setShowAudioConversation(false)}
        selectedVoice={selectedVoice}
        onVoiceChange={setSelectedVoice}
        existingConversation={(() => {
          const messages = conversation?.messages || [];
          console.log('🚀 Passing existingConversation to RealtimeAudioOverlay:', {
            conversationId: conversation?.id,
            messagesCount: messages.length,
            messages: messages.map(m => ({ role: m.role, content: m.content?.substring(0, 30) }))
          });
          return messages;
        })()}
        selectedModel={selectedModel || 'gpt-4o-audio-preview'}
        currentUser={currentUser}
        isFromWelcome={false}
        onUpdateConversation={(voiceMessage) => {
          console.log('🚀 Voice message callback called, conversation exists:', !!conversation);
          console.log('🚀 Voice message:', voiceMessage);
          if (onUpdateConversation) {
            // Use a callback to get the most current conversation state from the parent
            onUpdateConversation(conversation?.id || 'CREATE_NEW', {
              messages: voiceMessage // Send just the new message, let parent handle appending
            });
          }
        }}
      />
    )}

    {/* Native Realtime Audio Conversation Overlay */}
    {showNativeConversation && (
      <NativeRealtimeOverlay
        onClose={() => setShowNativeConversation(false)}
        selectedVoice={selectedVoice}
        onVoiceChange={setSelectedVoice}
        existingConversation={conversation?.messages || []}
        selectedModel={selectedModel || 'gpt-4o-realtime-preview-2024-12-17'}
      />
    )}

    {/* OpenAI Agents SDK Audio Conversation Overlay */}
    {showOpenAISDK && (
      <OpenAISDKOverlay
        onClose={() => setShowOpenAISDK(false)}
        selectedVoice={selectedVoice}
        onVoiceChange={setSelectedVoice}
        existingConversation={conversation?.messages || []}
        selectedModel={'gpt-4o-realtime-preview'}
      />
    )}

    {/* Voice Mode Selector */}
    {showVoiceSelector && (
      <VoiceModeSelector
        onClose={() => setShowVoiceSelector(false)}
        existingConversation={conversation?.messages || []}
        conversationId={conversation?.id}
        onSaveMessage={async (message) => {
          // Add message to conversation
          const newMessage = {
            ...message,
            id: Date.now().toString(),
            model: 'audio-chat'
          };
          
          // Update conversation with new message
          if (onUpdateConversation && conversation) {
            onUpdateConversation(conversation.id, {
              messages: [...(conversation?.messages || []), newMessage]
            });
          }
          
          // Save to database if conversation exists
          if (conversation?.id) {
            try {
              const apiUrl = process.env.NODE_ENV === 'development' 
                ? 'http://localhost:4001/api' 
                : 'https://api-chat.supple.tools/api';
              
              await fetch(`${apiUrl}/conversations/${conversation.id}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                  messages: [...(conversation?.messages || []), newMessage]
                }),
              });
            } catch (error) {
              console.error('Failed to save audio message:', error);
            }
          }
        }}
      />
    )}

    {/* Citation Preview Modal */}
    {showCitationPreview && selectedCitation && (
      <CitationPreview
        citationTitle={selectedCitation.title}
        contextSpaceId={selectedCitation.contextSpaceId}
        onClose={() => {
          setShowCitationPreview(false);
          setSelectedCitation(null);
        }}
      />
    )}

      </div>
      
      {/* Resource Preview Panel */}
      {viewingResource && viewingResource.resourceData && (
        <div className={`w-96 border-l h-full flex flex-col ${
          isSuppleMode 
            ? 'supple-border-subtle supple-card-glass backdrop-blur-md' 
            : 'border-gray-200 bg-white'
        }`}>
          {/* Resource Preview Header */}
          <div className={`p-4 border-b flex items-center justify-between ${
            isSuppleMode ? 'supple-border-subtle' : 'border-gray-200'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`${
                isSuppleMode ? 'supple-text-primary' : 'text-blue-600'
              }`}>
                {viewingResource.resourceData.type === 'URL' ? (
                  <Globe className="w-5 h-5" />
                ) : viewingResource.resourceData.type === 'FILE' ? (
                  <FileText className="w-5 h-5" />
                ) : (
                  <StickyNote className="w-5 h-5" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className={`text-sm font-semibold truncate ${
                  isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
                }`}>
                  {viewingResource.resourceData.title}
                </h3>
                <p className={`text-xs ${
                  isSuppleMode ? 'supple-text-muted' : 'text-gray-500'
                }`}>
                  {viewingResource.resourceData.type === 'URL' ? 'Website' : 
                   viewingResource.resourceData.type === 'FILE' ? 'File' : 'Note'}
                </p>
              </div>
            </div>
            <button
              onClick={onCloseResourcePreview}
              className={`p-2 rounded-lg transition-colors ${
                isSuppleMode 
                  ? 'supple-text-muted hover:supple-text-primary hover:supple-card-glass-hover' 
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
              title="Close Preview"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {/* Resource Preview Content */}
          <div className="flex-1 p-4 overflow-y-auto">
            {viewingResource.resourceData.description && (
              <div className={`mb-4 p-3 rounded-lg ${
                isSuppleMode ? 'supple-card-glass' : 'bg-gray-50'
              }`}>
                <h4 className={`text-xs font-medium mb-2 ${
                  isSuppleMode ? 'supple-text-primary' : 'text-gray-700'
                }`}>
                  Description
                </h4>
                <p className={`text-sm ${
                  isSuppleMode ? 'supple-text-muted' : 'text-gray-600'
                }`}>
                  {viewingResource.resourceData.description}
                </p>
              </div>
            )}
            
            {viewingResource.resourceData.type === 'URL' ? (
              <div className="space-y-4">
                <div className={`p-3 rounded-lg ${
                  isSuppleMode ? 'supple-card-glass' : 'bg-gray-50'
                }`}>
                  <h4 className={`text-xs font-medium mb-2 ${
                    isSuppleMode ? 'supple-text-primary' : 'text-gray-700'
                  }`}>
                    URL
                  </h4>
                  <a
                    href={viewingResource.resourceData.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-2 text-sm underline ${
                      isSuppleMode 
                        ? 'text-blue-300 hover:text-blue-200' 
                        : 'text-blue-600 hover:text-blue-800'
                    }`}
                  >
                    {viewingResource.resourceData.url}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
                
                {(viewingResource.resourceData.crawledContent || viewingResource.resourceData.content) && (
                  <div className={`p-3 rounded-lg ${
                    isSuppleMode ? 'supple-card-glass' : 'bg-gray-50'
                  }`}>
                    <h4 className={`text-xs font-medium mb-2 ${
                      isSuppleMode ? 'supple-text-primary' : 'text-gray-700'
                    }`}>
                      Content Preview
                    </h4>
                    <div className={`text-sm whitespace-pre-wrap max-h-96 overflow-y-auto ${
                      isSuppleMode ? 'supple-text-primary' : 'text-gray-800'
                    }`}>
                      {viewingResource.resourceData.crawledContent || viewingResource.resourceData.content || 'Content not available'}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className={`p-3 rounded-lg ${
                isSuppleMode ? 'supple-card-glass' : 'bg-gray-50'
              }`}>
                <h4 className={`text-xs font-medium mb-2 ${
                  isSuppleMode ? 'supple-text-primary' : 'text-gray-700'
                }`}>
                  Content
                </h4>
                <div className={`text-sm whitespace-pre-wrap ${
                  isSuppleMode ? 'supple-text-primary' : 'text-gray-800'
                }`}>
                  {viewingResource.resourceData.content || 'No content available'}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(ChatInterface);